import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Pair2Mark, RtaMark } from './PortalSelector';
import { Home as HomeIcon, ListChecks, MessageCircle, User, Sparkles, Mic, Phone, MapPin, Truck, Star, ArrowRight, Signal, Wifi, Battery, Send, CreditCard, Settings, Shield, Bell, LogOut, CheckCircle2, Clock, Briefcase, Snowflake, Sofa, Package, Wrench, Navigation as NavigationIcon, ChevronLeft, ChevronRight, HelpCircle, Camera, ImagePlus, Loader2, History, Plus, AlertTriangle, Gavel, Receipt, Download, X, AlertCircle, Ban, Hourglass, XCircle, MessageSquare, ImageIcon, Trash2 } from 'lucide-react';
import { detectServiceFromPrompt, getServiceConfig, ServiceId } from '../lib/serviceData';
import { getSlotsForService, parseInitialPrompt, parseUserReply, nextMissingSlot, formatAcknowledgement, getInitialGreeting, getCompletionMessage, Slot, SlotValues, ConversationTurn } from '../lib/agenticAgent';
import { useAuth, signIn, signOut } from '../lib/authStore';
export type MobileJobStatus = 'bidding_open' | 'pending_payment' | 'active' | 'completed' | 'cancelled';
export type MobileJobType = 'booking' | 'bidding';
export interface MobileJobBid {
  supplier: string;
  price: number;
  rating: number;
  initials: string;
  color: string;
  receivedAt: number;
  status?: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled';
  truck?: string;
}
export interface MobileJobRecord {
  id: string;
  type: MobileJobType;
  status: MobileJobStatus;
  serviceId: ServiceId;
  serviceName: string;
  prompt: string;
  routeHeadline: string;
  pickup?: string;
  dropoff?: string;
  supplierName?: string;
  supplierTruck?: string;
  price?: number;
  estimatedPrice?: number;
  createdAt: number;
  closesAt?: number;
  startedAt?: number;
  completedAt?: number;
  cancelledAt?: number;
  bids?: MobileJobBid[];
  rating?: number;
  reviewText?: string;
  ratingTags?: string[];
  ratedAt?: number;
  photos?: string[];
}
type ChatSession = {
  id: string;
  serviceId: ServiceId;
  prompt: string;
  createdAt: number;
  updatedAt: number;
  summary: string;
  lastMessage: string;
  lastFrom: 'ai' | 'user' | 'system';
  unread: boolean;
  status: 'active' | 'booked' | 'in_transit' | 'awaiting_rating' | 'completed' | 'expired';
};
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
const MOCK_MOBILE_JOBS: MobileJobRecord[] = [{
  id: 'TRP-9921',
  type: 'booking',
  status: 'active',
  serviceId: 'home',
  serviceName: 'Apartment Move',
  prompt: 'Moving a 2-bedroom apartment from Marina to Downtown',
  routeHeadline: 'Dubai Marina → Downtown Dubai',
  pickup: 'Marina Heights, T2',
  dropoff: 'JLT, Cluster J',
  supplierName: 'FastTrack Logistics',
  supplierTruck: '3-Ton Box Truck',
  price: 420,
  createdAt: Date.now() - 4 * 60 * 60 * 1000,
  startedAt: Date.now() - 30 * 60 * 1000
}, {
  id: 'TRP-9922',
  type: 'booking',
  status: 'pending_payment',
  serviceId: 'furniture',
  serviceName: 'Furniture Delivery',
  prompt: 'Pick up a sofa and dining set from IKEA Festival City',
  routeHeadline: 'Festival City → Jumeirah Village Circle',
  pickup: 'IKEA Festival City',
  dropoff: 'Jumeirah Village Circle',
  supplierName: 'BlueLine Express',
  supplierTruck: '1.5-Ton Pickup',
  price: 320,
  createdAt: Date.now() - 25 * 60 * 1000
}, {
  id: 'TRP-9920',
  type: 'booking',
  status: 'completed',
  serviceId: 'furniture',
  serviceName: 'Furniture Delivery',
  prompt: 'Deliver a new mattress from Al Quoz to Marina',
  routeHeadline: 'Al Quoz → Dubai Marina',
  pickup: 'Al Quoz',
  dropoff: 'Dubai Marina',
  supplierName: 'Omar N.',
  supplierTruck: '1-Ton Pickup',
  price: 180,
  createdAt: Date.now() - 2 * 24 * 60 * 60 * 1000,
  completedAt: Date.now() - 1 * 24 * 60 * 60 * 1000,
  rating: 5,
  reviewText: 'Great service, very careful with the mattress.',
  ratingTags: ['On time', 'Careful with items'],
  ratedAt: Date.now() - 23 * 60 * 60 * 1000
}, {
  id: 'TRP-9919',
  type: 'booking',
  status: 'completed',
  serviceId: 'recovery',
  serviceName: 'Car Recovery',
  prompt: 'Recover my car from a parking accident in Business Bay',
  routeHeadline: 'Business Bay → Al Quoz garage',
  pickup: 'Business Bay',
  dropoff: 'Al Quoz garage',
  supplierName: 'Sahara Recovery',
  supplierTruck: 'Flatbed recovery truck',
  price: 250,
  createdAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
  completedAt: Date.now() - 4 * 24 * 60 * 60 * 1000
}, {
  id: 'TRP-9918',
  type: 'booking',
  status: 'cancelled',
  serviceId: 'equipment',
  serviceName: 'Heavy Equipment Hauling',
  prompt: 'Move an excavator from Al Quoz to Hatta site',
  routeHeadline: 'Al Quoz → Hatta',
  pickup: 'Al Quoz',
  dropoff: 'Hatta',
  supplierName: 'HeavyMove UAE',
  supplierTruck: 'Low-bed 40-Ton',
  price: 2400,
  createdAt: Date.now() - 10 * 24 * 60 * 60 * 1000,
  cancelledAt: Date.now() - 9 * 24 * 60 * 60 * 1000
}, {
  id: 'TRP-9923',
  type: 'bidding',
  status: 'bidding_open',
  serviceId: 'delivery',
  serviceName: 'Construction Materials',
  prompt: 'Need to move 12m steel beams from DIP to a tower site in Business Bay tomorrow morning',
  routeHeadline: 'Dubai Investments Park → Business Bay',
  pickup: 'Dubai Investments Park',
  dropoff: 'Business Bay',
  estimatedPrice: 1450,
  createdAt: Date.now() - 3 * 60 * 60 * 1000,
  closesAt: Date.now() + 21 * 60 * 60 * 1000,
  bids: [{
    supplier: 'Al Khaleej Movers',
    price: 1280,
    rating: 4.6,
    initials: 'AK',
    color: 'bg-amber-100 text-amber-700',
    receivedAt: Date.now() - 2 * 60 * 60 * 1000
  }, {
    supplier: 'BlueLine Express',
    price: 1190,
    rating: 4.8,
    initials: 'BL',
    color: 'bg-blue-100 text-blue-700',
    receivedAt: Date.now() - 90 * 60 * 1000
  }, {
    supplier: 'Sahara Logistics',
    price: 1240,
    rating: 4.7,
    initials: 'SL',
    color: 'bg-rose-100 text-rose-700',
    receivedAt: Date.now() - 45 * 60 * 1000
  }]
}, {
  id: 'TRP-9924',
  type: 'bidding',
  status: 'completed',
  serviceId: 'home',
  serviceName: 'Home Moving',
  prompt: 'Studio relocation from JLT to Silicon Oasis',
  routeHeadline: 'JLT → Dubai Silicon Oasis',
  pickup: 'JLT',
  dropoff: 'Dubai Silicon Oasis',
  supplierName: 'Sahara Logistics',
  supplierTruck: '2-Ton Box Truck',
  price: 620,
  estimatedPrice: 720,
  createdAt: Date.now() - 15 * 24 * 60 * 60 * 1000,
  completedAt: Date.now() - 14 * 24 * 60 * 60 * 1000,
  bids: [{
    supplier: 'Sahara Logistics',
    price: 620,
    rating: 4.7,
    initials: 'SL',
    color: 'bg-rose-100 text-rose-700',
    receivedAt: Date.now() - 15 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000,
    status: 'accepted'
  }]
}];
const MOCK_CHATS: ChatSession[] = [{
  id: 'chat-001',
  serviceId: 'home',
  prompt: 'Need to move a 3-bedroom apartment from Marina to JLT this weekend',
  createdAt: Date.now() - 25 * 60 * 1000,
  updatedAt: Date.now() - 3 * 60 * 1000,
  summary: 'Home move · Marina → JLT',
  lastMessage: '4 carriers sent quotes — Swift Movers offers AED 420',
  lastFrom: 'ai',
  unread: true,
  status: 'active'
}, {
  id: 'chat-002',
  serviceId: 'reefer',
  prompt: 'Refrigerated cargo, 2 pallets, frozen meat, -18°C',
  createdAt: Date.now() - 4 * 60 * 60 * 1000,
  updatedAt: Date.now() - 2 * 60 * 60 * 1000,
  summary: 'Refrigerated · 2 pallets · -18°C',
  lastMessage: 'You: Counter offer to Cool Chain Co.: AED 850',
  lastFrom: 'user',
  unread: false,
  status: 'active'
}, {
  id: 'chat-003',
  serviceId: 'furniture',
  prompt: 'Furniture delivery from IKEA Festival City to Downtown',
  createdAt: Date.now() - 6 * 60 * 60 * 1000,
  updatedAt: Date.now() - 45 * 60 * 1000,
  summary: 'Furniture · IKEA → Downtown',
  lastMessage: 'Driver Omar N. is on the way · ETA 18 min',
  lastFrom: 'system',
  unread: true,
  status: 'in_transit'
}, {
  id: 'chat-004',
  serviceId: 'delivery',
  prompt: 'Pick up 3 pallets from Jebel Ali warehouse to Sharjah',
  createdAt: Date.now() - 10 * 60 * 60 * 1000,
  updatedAt: Date.now() - 8 * 60 * 60 * 1000,
  summary: 'Pallets · Jebel Ali → Sharjah',
  lastMessage: 'Booked with GulfCargo · AED 540 · Pickup 8 AM tomorrow',
  lastFrom: 'system',
  unread: false,
  status: 'booked'
}, {
  id: 'chat-005',
  serviceId: 'home',
  prompt: 'Studio relocation from JLT to Silicon Oasis',
  createdAt: Date.now() - 26 * 60 * 60 * 1000,
  updatedAt: Date.now() - 22 * 60 * 60 * 1000,
  summary: 'Studio move · JLT → Silicon Oasis',
  lastMessage: 'Delivered ✓ How was your move with Sahara Logistics?',
  lastFrom: 'system',
  unread: true,
  status: 'awaiting_rating'
}, {
  id: 'chat-006',
  serviceId: 'furniture',
  prompt: 'Sofa delivery from Dragon Mart to Mirdif',
  createdAt: Date.now() - 3 * 24 * 60 * 60 * 1000,
  updatedAt: Date.now() - 3 * 24 * 60 * 60 * 1000 + 4 * 60 * 60 * 1000,
  summary: 'Sofa · Dragon Mart → Mirdif',
  lastMessage: 'You rated BlueLine Express ★★★★★ — thanks!',
  lastFrom: 'system',
  unread: false,
  status: 'completed'
}, {
  id: 'chat-007',
  serviceId: 'recovery',
  prompt: 'Car broken down on SZR, need recovery to Al Quoz workshop',
  createdAt: Date.now() - 30 * 60 * 60 * 1000,
  updatedAt: Date.now() - 30 * 60 * 60 * 1000,
  summary: 'Recovery · SZR → Al Quoz',
  lastMessage: 'Quotes expired — start a new chat to rebook',
  lastFrom: 'system',
  unread: false,
  status: 'expired'
}];
type CarrierOffer = {
  id: string;
  name: string;
  rating: number;
  reviews: number;
  vehicle: string;
  price: number;
  eta: string;
  verified?: boolean;
  fast?: boolean;
};
const MOCK_OFFERS: CarrierOffer[] = [{
  id: 'off-1',
  name: 'Swift Movers LLC',
  rating: 4.9,
  reviews: 248,
  vehicle: '7-Ton Truck',
  price: 420,
  eta: '25 min',
  verified: true,
  fast: true
}, {
  id: 'off-2',
  name: 'Premier Logistics',
  rating: 4.8,
  reviews: 192,
  vehicle: '5-Ton Truck',
  price: 450,
  eta: '32 min',
  verified: true
}, {
  id: 'off-3',
  name: 'Falcon Express',
  rating: 4.7,
  reviews: 156,
  vehicle: '7-Ton Truck',
  price: 380,
  eta: '40 min'
}, {
  id: 'off-4',
  name: 'Desert Star Transport',
  rating: 4.6,
  reviews: 88,
  vehicle: '5-Ton Truck',
  price: 410,
  eta: '35 min'
}];
type BidEntry = {
  id: string;
  carrier: string;
  rating: number;
  price: number;
};
const BID_CARRIERS: Array<{
  name: string;
  rating: number;
}> = [{
  name: 'Rapid Cargo Co.',
  rating: 4.8
}, {
  name: 'BlueLine Transport',
  rating: 4.5
}, {
  name: 'Pioneer Movers',
  rating: 4.9
}, {
  name: 'GoldenHaul Freight',
  rating: 4.6
}, {
  name: 'Atlas Logistics',
  rating: 4.7
}, {
  name: 'Nova Cargo',
  rating: 4.4
}];
type OfferStage = 'offers' | 'counter-waiting' | 'counter-response' | 'bidding-live' | 'bidding-complete' | 'confirmed';
const SERVICES = [{
  id: 'recovery',
  label: 'Recovery',
  icon: Truck,
  color: 'red'
}, {
  id: 'home',
  label: 'Home Move',
  icon: HomeIcon,
  color: 'emerald'
}, {
  id: 'furniture',
  label: 'Furniture',
  icon: Sofa,
  color: 'sky'
}, {
  id: 'delivery',
  label: 'Delivery',
  icon: Package,
  color: 'amber'
}, {
  id: 'reefer',
  label: 'Refrigerated',
  icon: Snowflake,
  color: 'cyan'
}, {
  id: 'equipment',
  label: 'Heavy Equip.',
  icon: Wrench,
  color: 'fuchsia'
}] as const;
const COLOR_MAP: Record<string, string> = {
  red: 'bg-red-50 text-red-500',
  emerald: 'bg-emerald-50 text-emerald-500',
  sky: 'bg-sky-50 text-sky-500',
  amber: 'bg-amber-50 text-amber-600',
  cyan: 'bg-cyan-50 text-cyan-600',
  fuchsia: 'bg-fuchsia-50 text-fuchsia-600'
};
const DISCOVERY_CHIPS = [{
  label: 'Move home',
  icon: HomeIcon
}, {
  label: 'Furniture delivery',
  icon: Sofa
}, {
  label: 'Car recovery',
  icon: Truck
}, {
  label: 'Pallets / cargo',
  icon: Package
}, {
  label: 'Refrigerated load',
  icon: Snowflake
}, {
  label: 'Heavy equipment',
  icon: Wrench
}];
function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours < 1) {
    const mins = Math.max(1, Math.floor(diff / (60 * 1000)));
    return `${mins}m ago`;
  }
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
type ChatView = 'list' | 'new' | 'thread';
export function MobileApp() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [authStage, setAuthStage] = useState<'intro' | 'splash' | 'phone' | 'otp'>('intro');
  const [pendingPhone, setPendingPhone] = useState('');
  const [tab, setTab] = useState<'home' | 'trips' | 'chat' | 'profile'>('home');
  const [jobs, setJobs] = useState<MobileJobRecord[]>(MOCK_MOBILE_JOBS);
  const [chats, setChats] = useState<ChatSession[]>(MOCK_CHATS);
  const [chatView, setChatView] = useState<ChatView>('list');
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [activeService, setActiveService] = useState<ServiceId | null>(null);
  const [initialPrompt, setInitialPrompt] = useState<string>('');
  const activeChat = activeChatId ? chats.find(c => c.id === activeChatId) || null : null;
  const startNewChat = (serviceId: ServiceId, prompt: string) => {
    const newChat: ChatSession = {
      id: `chat-${Date.now()}`,
      serviceId,
      prompt,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      summary: prompt.slice(0, 60),
      lastMessage: prompt,
      lastFrom: 'user',
      unread: false,
      status: 'active'
    };
    setChats(prev => [newChat, ...prev]);
    setActiveChatId(newChat.id);
    setActiveService(serviceId);
    setInitialPrompt(prompt);
    setChatView('thread');
  };
  const handleStartChat = (serviceId?: ServiceId, prompt?: string) => {
    if (serviceId) {
      startNewChat(serviceId, prompt || '');
    } else {
      // From home AI prompt → open compose
      setActiveChatId(null);
      setActiveService(null);
      setInitialPrompt('');
      setChatView('new');
    }
    setTab('chat');
  };
  const handleDiscoverySubmit = (text: string) => {
    const detected = detectServiceFromPrompt(text);
    startNewChat(detected, text);
  };
  const handleComposeNewChat = () => {
    setActiveChatId(null);
    setActiveService(null);
    setInitialPrompt('');
    setChatView('new');
  };
  const handleBackToList = () => {
    setActiveChatId(null);
    setActiveService(null);
    setInitialPrompt('');
    setChatView('list');
  };
  const handleResumeChat = (chat: ChatSession) => {
    setActiveChatId(chat.id);
    setActiveService(chat.serviceId);
    setInitialPrompt(chat.prompt);
    setChatView('thread');
    // Clear unread when opening
    if (chat.unread) {
      setChats(prev => prev.map(c => c.id === chat.id ? {
        ...c,
        unread: false
      } : c));
    }
  };
  const handleDeleteChat = (chatId: string) => {
    setChats(prev => prev.filter(c => c.id !== chatId));
    if (activeChatId === chatId) {
      setActiveChatId(null);
      setChatView('list');
    }
  };
  const handleChatBooked = (job: MobileJobRecord) => {
    setJobs(prev => [job, ...prev]);
    if (activeChatId) {
      setChats(prev => prev.map(c => c.id === activeChatId ? {
        ...c,
        status: 'booked',
        updatedAt: Date.now(),
        lastMessage: `Booked with ${job.supplierName || 'carrier'} · AED ${job.price}`,
        lastFrom: 'system'
      } : c));
    }
  };
  // When user taps Chat tab from bottom nav, default to list view
  useEffect(() => {
    if (tab !== 'chat') return;
    if (chatView === 'thread' && !activeChatId) setChatView('list');
  }, [tab, chatView, activeChatId]);
  return <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans" data-id="element-1801">
      {/* Top chrome with branding */}
      <div className="absolute top-0 inset-x-0 px-4 py-4 flex items-center justify-between gap-4 z-10" data-id="element-1802">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white hover:bg-gray-50 ring-1 ring-gray-200 text-gray-700 text-xs font-bold transition-colors shadow-sm" data-id="element-1803">
          <ChevronLeft className="w-3.5 h-3.5" data-id="element-1804" />
          All modules
        </button>
        <div className="hidden sm:flex items-center gap-4 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md ring-1 ring-gray-200 shadow-sm" data-id="element-1805">
          <Pair2Mark small data-id="element-1806" />
          <span className="w-px h-6 bg-gray-200" data-id="element-1807" />
          <RtaMark small data-id="element-1808" />
        </div>
      </div>
      <div className="w-[375px] h-[812px] bg-white rounded-[3rem] shadow-2xl overflow-hidden border-[10px] border-gray-900 relative flex flex-col" data-id="element-1809">
        <div className="h-11 w-full flex justify-between items-center px-7 pt-2 bg-white z-20 text-[13px] font-semibold shrink-0" data-id="element-1810">
          <span data-id="element-1811">9:41</span>
          <div className="w-32 h-7 bg-black rounded-full absolute left-1/2 -translate-x-1/2 top-2" data-id="element-1812"></div>
          <div className="flex items-center gap-1.5" data-id="element-1813">
            <Signal className="w-3.5 h-3.5" data-id="element-1814" />
            <Wifi className="w-3.5 h-3.5" data-id="element-1815" />
            <Battery className="w-4 h-4" data-id="element-1816" />
          </div>
        </div>

        {!user ? <SignInFlow stage={authStage} phone={pendingPhone} onSetStage={setAuthStage} onSetPhone={setPendingPhone} onComplete={p => {
        signIn(p);
        setAuthStage('intro');
      }} onGuest={() => {
        signIn('+971 50 000 0000', 'Guest');
        setAuthStage('intro');
      }} data-id="element-1817" /> : <>
            <div className="flex-1 overflow-y-auto scrollbar-hide bg-gradient-to-b from-white to-gray-50" data-id="element-1818">
              <AnimatePresence mode="popLayout" initial={false} data-id="element-1819">
                <motion.div key={tab} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.18
            }} className="h-full" data-id="element-1820">
                  {tab === 'home' && <HomeTab jobs={jobs} onStartChat={(s, p) => handleStartChat(s, p)} data-id="element-1821" />}
                  {tab === 'trips' && <TripsTab jobs={jobs} setJobs={setJobs} onStartChat={handleStartChat} data-id="element-1822" />}
                  {tab === 'chat' && chatView === 'list' && <ChatThreadsList chats={chats} onResume={handleResumeChat} onCompose={handleComposeNewChat} onDelete={handleDeleteChat} data-id="element-1823" />}
                  {tab === 'chat' && chatView === 'new' && <NewChatPrompt onSubmit={handleDiscoverySubmit} onBack={handleBackToList} data-id="element-1824" />}
                  {tab === 'chat' && chatView === 'thread' && activeChat && activeService !== null && <AgenticChatTab key={activeChatId} chatId={activeChatId} serviceId={activeService} initialPrompt={initialPrompt} createdAt={activeChat.createdAt} onBack={handleBackToList} onNewChat={handleComposeNewChat} onBook={handleChatBooked} onViewJobs={() => setTab('trips')} data-id="element-1825" />}
                  {tab === 'profile' && <ProfileTab jobs={jobs} onSignOut={() => {
                signOut();
                setTab('home');
              }} data-id="element-1826" />}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="shrink-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 px-6 pt-2 pb-6 flex justify-around" data-id="element-1827">
              {([{
            id: 'home',
            label: 'Home',
            icon: HomeIcon
          }, {
            id: 'trips',
            label: 'Jobs',
            icon: ListChecks
          }, {
            id: 'chat',
            label: 'Requests',
            icon: MessageCircle
          }, {
            id: 'profile',
            label: 'Profile',
            icon: User
          }] as Array<{
            id: 'home' | 'trips' | 'chat' | 'profile';
            label: string;
            icon: typeof HomeIcon;
          }>).map(it => {
            const Icon = it.icon;
            const active = tab === it.id;
            return <button key={it.id} onClick={() => {
              setTab(it.id);
              if (it.id === 'chat' && chatView === 'new') {
                setChatView('list');
              }
            }} className="flex flex-col items-center gap-1 transition-colors" data-id="element-1828">
                    <Icon className={`w-5 h-5 ${active ? 'text-brand-600' : 'text-gray-400'}`} data-id="element-1829" />
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? 'text-brand-600' : 'text-gray-400'}`} data-id="element-1830">
                      {it.label}
                    </span>
                  </button>;
          })}
            </div>
          </>}
      </div>
    </div>;
}
type AuthStage = 'intro' | 'splash' | 'phone' | 'otp';
function SignInFlow({
  stage,
  phone,
  onSetStage,
  onSetPhone,
  onComplete,
  onGuest
}: {
  stage: AuthStage;
  phone: string;
  onSetStage: (s: AuthStage) => void;
  onSetPhone: (p: string) => void;
  onComplete: (phone: string) => void;
  onGuest: () => void;
}) {
  return <div className="flex-1 relative overflow-hidden bg-white text-gray-900" data-id="element-1831">
      <AnimatePresence mode="wait" initial={false} data-id="element-1832">
        {stage === 'intro' && <motion.div key="intro" initial={{
        opacity: 1
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 1.08,
        filter: 'blur(8px)'
      }} transition={{
        duration: 0.5,
        ease: [0.65, 0, 0.35, 1]
      }} className="absolute inset-0" data-id="element-1833">
            <SplashIntro onComplete={() => onSetStage('splash')} data-id="element-1834" />
          </motion.div>}
        {stage === 'splash' && <motion.div key="splash" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.35
      }} className="absolute inset-0" data-id="element-1835">
            <SignInSplash onStart={() => onSetStage('phone')} onGuest={onGuest} data-id="element-1836" />
          </motion.div>}
        {stage === 'phone' && <motion.div key="phone" initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -30
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0" data-id="element-1837">
            <SignInPhone initialPhone={phone} onBack={() => onSetStage('splash')} onSubmit={p => {
          onSetPhone(p);
          onSetStage('otp');
        }} data-id="element-1838" />
          </motion.div>}
        {stage === 'otp' && <motion.div key="otp" initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -30
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0" data-id="element-1839">
            <SignInOtp phone={phone} onBack={() => onSetStage('phone')} onResend={() => {}} onSubmit={() => onComplete(phone)} data-id="element-1840" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function SplashIntro({
  onComplete
}: {
  onComplete: () => void;
}) {
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState<0 | 1 | 2 | 3>(0);
  useEffect(() => {
    if (reduce) {
      const t = setTimeout(onComplete, 900);
      return () => clearTimeout(t);
    }
    const timers = [setTimeout(() => setPhase(1), 250), setTimeout(() => setPhase(2), 900), setTimeout(() => setPhase(3), 1700), setTimeout(() => onComplete(), 2900)];
    return () => timers.forEach(clearTimeout);
  }, [reduce, onComplete]);
  return <button type="button" onClick={onComplete} aria-label="Skip intro" className="relative w-full h-full overflow-hidden bg-surface-100 text-gray-900 block focus:outline-none" data-id="element-1841">
      {/* Refined pearl backdrop */}
      <div className="absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 35%, #fafaf9 0%, #f5f5f4 45%, #ffffff 100%)'
    }} data-id="element-1842" />

      {/* Center stage */}
      <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1843">
        {/* Halo */}
        <motion.div initial={{
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: phase >= 1 ? 1 : 0
      }} transition={{
        duration: 0.6,
        ease: [0.65, 0, 0.35, 1]
      }} className="absolute w-72 h-72 rounded-full pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,107,53,0.22) 0%, rgba(255,107,53,0) 65%)'
      }} data-id="element-1844" />
        {/* Inner faint ring */}
        <motion.div initial={{
        scale: 0.7,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: phase >= 1 ? 1 : 0
      }} transition={{
        duration: 0.7,
        delay: 0.05
      }} className="absolute w-44 h-44 rounded-full border border-brand-200/70 pointer-events-none" data-id="element-1845" />
        <motion.div initial={{
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: phase >= 2 ? 0.8 : 0
      }} transition={{
        duration: 0.6
      }} className="absolute w-64 h-64 rounded-full border border-brand-100 pointer-events-none" data-id="element-1846" />

        {/* Core orb */}
        <motion.div initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: phase >= 3 ? 0.7 : 1,
        opacity: 1
      }} transition={{
        scale: {
          duration: 0.7,
          ease: [0.65, 0, 0.35, 1]
        },
        opacity: {
          duration: 0.4
        }
      }} className="relative w-24 h-24 rounded-3xl orange-gradient-bg flex items-center justify-center will-change-transform" style={{
        boxShadow: '0 20px 50px -10px rgba(255,107,53,0.55), 0 0 0 1px rgba(255,255,255,0.6) inset'
      }} data-id="element-1847">
          <motion.div initial={{
          scale: 0,
          rotate: -30
        }} animate={{
          scale: 1,
          rotate: 0
        }} transition={{
          delay: 0.35,
          type: 'spring',
          stiffness: 220,
          damping: 18
        }} data-id="element-1848">
            <Sparkles className="w-10 h-10 text-white" data-id="element-1849" />
          </motion.div>
        </motion.div>

        {/* Wordmark */}
        <AnimatePresence data-id="element-1850">
          {phase >= 3 && <motion.div key="wordmark" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.5,
          ease: [0.65, 0, 0.35, 1]
        }} className="absolute top-1/2 mt-20 text-center will-change-transform" data-id="element-1851">
              <h1 className="text-5xl font-display font-black tracking-tight leading-none text-gray-900" data-id="element-1852">
                PAIR<span className="orange-gradient-text" data-id="element-1853">2</span>
              </h1>
              <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            delay: 0.25,
            duration: 0.55,
            ease: 'easeOut'
          }} className="h-px w-28 mx-auto mt-3 bg-gradient-to-r from-transparent via-brand-400 to-transparent origin-center" data-id="element-1854" />
              <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4,
            duration: 0.35
          }} className="mt-3 text-[10px] font-bold uppercase tracking-[0.38em] text-gray-500" data-id="element-1855">
                Move&nbsp;·&nbsp;The&nbsp;·&nbsp;World
              </motion.p>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Top status chip */}
      <motion.div initial={{
      opacity: 0,
      y: -6
    }} animate={{
      opacity: phase >= 2 ? 1 : 0,
      y: 0
    }} transition={{
      duration: 0.35
    }} className="absolute top-7 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-200 shadow-sm" data-id="element-1856">
        <span className="w-1 h-1 rounded-full bg-emerald-500" data-id="element-1857" />
        <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-gray-600" data-id="element-1858">
          Pair2 Marketplace
        </span>
      </motion.div>

      {/* Bottom hint */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: phase >= 3 ? 1 : 0
    }} transition={{
      duration: 0.5
    }} className="absolute bottom-10 left-1/2 -translate-x-1/2 text-center" data-id="element-1859">
        <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gray-400" data-id="element-1860">
          Tap to enter
        </p>
      </motion.div>
    </button>;
}
function SignInSplash({
  onStart,
  onGuest
}: {
  onStart: () => void;
  onGuest: () => void;
}) {
  return <div className="relative w-full h-full flex flex-col items-center justify-between px-7 pt-10 pb-8 bg-surface-100 overflow-hidden" data-id="element-1861">
      {/* Refined pearl backdrop */}
      <div className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 20%, #fafaf9 0%, #f5f5f4 35%, #ffffff 80%)'
    }} data-id="element-1862" />
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[460px] h-[460px] bg-stone-200/50 rounded-full blur-3xl" data-id="element-1863" />
      <div className="pointer-events-none absolute -bottom-40 -right-20 w-[340px] h-[340px] bg-amber-50/70 rounded-full blur-3xl" data-id="element-1864" />

      {/* Top brand mark */}
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm text-[10px] font-bold uppercase tracking-[0.18em] text-gray-700" data-id="element-1865">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1866" />
        Pair2 · Marketplace
      </motion.div>

      {/* Center */}
      <div className="relative flex flex-col items-center" data-id="element-1867">
        <motion.div initial={{
        opacity: 0,
        scale: 0.6
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.15,
        duration: 0.55,
        ease: [0.65, 0, 0.35, 1]
      }} className="relative w-44 h-44 flex items-center justify-center mb-6" data-id="element-1868">
          <div className="absolute w-44 h-44 rounded-full bg-stone-100" data-id="element-1869" />
          <div className="absolute w-32 h-32 rounded-full bg-stone-200/70" data-id="element-1870" />
          <motion.div animate={{
          scale: [1, 1.04, 1]
        }} transition={{
          duration: 2.4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="relative w-24 h-24 rounded-3xl orange-gradient-bg flex items-center justify-center will-change-transform" style={{
          boxShadow: '0 18px 45px -10px rgba(255,107,53,0.55), 0 0 0 1px rgba(255,255,255,0.5) inset'
        }} data-id="element-1871">
            <Sparkles className="w-10 h-10 text-white" data-id="element-1872" />
          </motion.div>
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="text-3xl font-display font-black text-center leading-tight text-gray-900" data-id="element-1873">
          Move anything.
          <br data-id="element-1874" />
          <span className="orange-gradient-text" data-id="element-1875">Instantly.</span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.45
      }} className="mt-3 text-center text-sm text-gray-600 max-w-[280px] leading-relaxed" data-id="element-1876">
          AI-matched carriers, live bidding, and end-to-end tracking — all from
          one app.
        </motion.p>
      </div>

      {/* CTAs */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.55
    }} className="relative w-full space-y-2.5" data-id="element-1877">
        <button onClick={onStart} className="w-full py-4 orange-gradient-bg text-white rounded-2xl font-bold text-sm shadow-[0_12px_36px_-12px_rgba(255,107,53,0.55)] active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-1878">
          Get started
          <ArrowRight className="w-4 h-4" data-id="element-1879" />
        </button>
        <button onClick={onGuest} className="w-full py-3.5 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 rounded-2xl font-bold text-xs transition-colors" data-id="element-1880">
          Continue as guest
        </button>
        <p className="text-[10px] text-center text-gray-500 leading-snug pt-1" data-id="element-1881">
          By continuing you agree to our{' '}
          <span className="text-gray-700 underline-offset-2 underline" data-id="element-1882">
            Terms
          </span>{' '}
          &amp;{' '}
          <span className="text-gray-700 underline-offset-2 underline" data-id="element-1883">
            Privacy
          </span>
          .
        </p>
      </motion.div>
    </div>;
}
function SignInPhone({
  initialPhone,
  onBack,
  onSubmit
}: {
  initialPhone: string;
  onBack: () => void;
  onSubmit: (phone: string) => void;
}) {
  const [country, setCountry] = useState('+971');
  const [number, setNumber] = useState(initialPhone.replace(/^\+\d+\s*/, '') || '');
  const isValid = number.replace(/\D/g, '').length >= 7;
  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit(`${country} ${number.trim()}`);
  };
  return <div className="relative w-full h-full flex flex-col bg-surface-100 text-gray-900 overflow-hidden" data-id="element-1884">
      <div className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 0%, #f5f5f4 0%, rgba(255,255,255,0) 55%)'
    }} data-id="element-1885" />
      {/* Top bar */}
      <div className="relative px-5 pt-5 pb-2 flex items-center justify-between" data-id="element-1886">
        <button onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors text-gray-700" aria-label="Back" data-id="element-1887">
          <ChevronLeft className="w-4 h-4" data-id="element-1888" />
        </button>
        <div className="flex items-center gap-1" data-id="element-1889">
          {[0, 1, 2].map(i => <div key={i} className={`h-1 rounded-full transition-all ${i === 0 ? 'w-6 bg-brand-500' : 'w-1.5 bg-gray-200'}`} data-id="element-1890" />)}
        </div>
        <div className="w-9" data-id="element-1891" />
      </div>

      {/* Content */}
      <div className="flex-1 px-7 pt-8 flex flex-col items-center text-center" data-id="element-1892">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-600 mb-2" data-id="element-1893">
          Step 1 of 2
        </p>
        <h1 className="text-2xl font-display font-bold leading-tight text-gray-900" data-id="element-1894">
          What's your{' '}
          <span className="orange-gradient-text" data-id="element-1895">mobile number?</span>
        </h1>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-[260px]" data-id="element-1896">
          We'll text a 6-digit code to verify it. Standard rates may apply.
        </p>

        <div className="mt-8 w-full flex gap-2" data-id="element-1897">
          <div className="relative shrink-0" data-id="element-1898">
            <select value={country} onChange={e => setCountry(e.target.value)} className="appearance-none bg-white border border-gray-200 text-gray-900 text-sm font-bold rounded-xl pl-3 pr-7 py-3.5 focus:outline-none focus:border-brand-400 transition-colors shadow-sm" data-id="element-1899">
              <option value="+971" data-id="element-1900">🇦🇪 +971</option>
              <option value="+966" data-id="element-1901">🇸🇦 +966</option>
              <option value="+91" data-id="element-1902">🇮🇳 +91</option>
              <option value="+44" data-id="element-1903">🇬🇧 +44</option>
              <option value="+1" data-id="element-1904">🇺🇸 +1</option>
            </select>
            <ChevronRight className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90 pointer-events-none" data-id="element-1905" />
          </div>
          <input type="tel" inputMode="numeric" placeholder="50 123 4567" value={number} onChange={e => setNumber(e.target.value.replace(/[^\d\s]/g, ''))} onKeyDown={e => e.key === 'Enter' && handleSubmit()} autoFocus className="flex-1 min-w-0 bg-white border border-gray-200 text-gray-900 text-base font-semibold tabular-nums rounded-xl px-4 py-3.5 text-center focus:outline-none focus:border-brand-400 placeholder:text-gray-400 transition-colors shadow-sm" data-id="element-1906" />
        </div>

        <div className="mt-6 flex items-start gap-2 text-[11px] text-gray-500 text-left max-w-[280px]" data-id="element-1907">
          <Shield className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" data-id="element-1908" />
          <p className="leading-snug" data-id="element-1909">
            Your number is encrypted end-to-end and never shared with carriers
            until you book.
          </p>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="relative px-5 pb-7 pt-3" data-id="element-1910">
        <button onClick={handleSubmit} disabled={!isValid} className="w-full py-4 orange-gradient-bg text-white rounded-2xl font-bold text-sm shadow-[0_12px_36px_-12px_rgba(255,107,53,0.55)] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:shadow-none" data-id="element-1911">
          Send code
          <ArrowRight className="w-4 h-4" data-id="element-1912" />
        </button>
      </div>
    </div>;
}
function SignInOtp({
  phone,
  onBack,
  onResend,
  onSubmit
}: {
  phone: string;
  onBack: () => void;
  onResend: () => void;
  onSubmit: () => void;
}) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(30);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);
  const allFilled = code.every(c => c !== '');
  useEffect(() => {
    if (!allFilled) return;
    setVerifying(true);
    const t = setTimeout(() => {
      onSubmitRef.current();
    }, 900);
    return () => clearTimeout(t);
  }, [allFilled]);
  const setDigit = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    setCode(c => {
      const next = [...c];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };
  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };
  const handleResend = () => {
    setResendIn(30);
    setCode(['', '', '', '', '', '']);
    inputsRef.current[0]?.focus();
    onResend();
  };
  return <div className="relative w-full h-full flex flex-col bg-surface-100 text-gray-900 overflow-hidden" data-id="element-1913">
      <div className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 0%, #f5f5f4 0%, rgba(255,255,255,0) 55%)'
    }} data-id="element-1914" />
      <div className="relative px-5 pt-5 pb-2 flex items-center justify-between" data-id="element-1915">
        <button onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors text-gray-700" aria-label="Back" data-id="element-1916">
          <ChevronLeft className="w-4 h-4" data-id="element-1917" />
        </button>
        <div className="flex items-center gap-1" data-id="element-1918">
          {[0, 1, 2].map(i => <div key={i} className={`h-1 rounded-full transition-all ${i <= 1 ? 'w-6 bg-brand-500' : 'w-1.5 bg-gray-200'}`} data-id="element-1919" />)}
        </div>
        <div className="w-9" data-id="element-1920" />
      </div>
      <div className="flex-1 px-7 pt-8 flex flex-col items-center text-center" data-id="element-1921">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-600 mb-2" data-id="element-1922">
          Step 2 of 2
        </p>
        <h1 className="text-2xl font-display font-bold leading-tight text-gray-900" data-id="element-1923">
          Enter the <span className="orange-gradient-text" data-id="element-1924">6-digit code</span>
        </h1>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-[280px]" data-id="element-1925">
          Sent to <span className="text-gray-900 font-semibold" data-id="element-1926">{phone}</span>.{' '}
          <button onClick={onBack} className="text-brand-600 hover:text-brand-700 underline-offset-2 underline" data-id="element-1927">
            Wrong number?
          </button>
        </p>

        <div className="mt-8 w-full flex gap-2 justify-between" data-id="element-1928">
          {code.map((digit, idx) => <input key={idx} ref={el => {
          inputsRef.current[idx] = el;
        }} value={digit} onChange={e => setDigit(idx, e.target.value)} onKeyDown={e => handleKeyDown(idx, e)} inputMode="numeric" maxLength={1} disabled={verifying} autoFocus={idx === 0} className={`w-11 h-14 text-center text-xl font-display font-bold tabular-nums bg-white border-2 rounded-xl focus:outline-none transition-colors shadow-sm ${digit ? 'border-brand-400 text-gray-900' : 'border-gray-200 text-gray-400'} ${verifying ? 'opacity-60' : ''}`} data-id="element-1929" />)}
        </div>

        {verifying ? <div className="mt-6 flex items-center justify-center gap-2 text-sm text-emerald-600 font-semibold" data-id="element-1930">
            <Loader2 className="w-4 h-4 animate-spin" data-id="element-1931" />
            Verifying…
          </div> : <div className="mt-6 text-center text-xs text-gray-500" data-id="element-1932">
            {resendIn > 0 ? <span data-id="element-1933">
                Didn't get it? Resend in{' '}
                <span className="text-gray-900 font-semibold tabular-nums" data-id="element-1934">
                  {resendIn}s
                </span>
              </span> : <button onClick={handleResend} className="text-brand-600 hover:text-brand-700 font-semibold" data-id="element-1935">
                Resend code
              </button>}
          </div>}
      </div>
      <div className="relative px-5 pb-7 pt-3" data-id="element-1936">
        <div className="flex items-start gap-2 text-[10px] text-gray-500 mb-3 max-w-[280px] mx-auto" data-id="element-1937">
          <Shield className="w-3 h-3 mt-0.5 text-emerald-500 shrink-0" data-id="element-1938" />
          <p className="leading-snug" data-id="element-1939">
            For your security, the code expires in 5 minutes.
          </p>
        </div>
      </div>
    </div>;
}
function ProfileTab({
  jobs,
  onSignOut
}: {
  jobs: MobileJobRecord[];
  onSignOut: () => void;
}) {
  type ProfileSheet = null | 'edit' | 'payments' | 'addresses' | 'notifications' | 'help' | 'privacy' | 'signout';
  const {
    user
  } = useAuth();
  const [sheet, setSheet] = useState<ProfileSheet>(null);
  const displayName = user?.name || 'Guest';
  const phone = user?.phone || '+971 50 000 0000';
  const initial = displayName.charAt(0).toUpperCase();
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const totalSpent = completedJobs.reduce((sum, j) => sum + (j.price || 0), 0);
  const ratingsGiven = completedJobs.filter(j => j.rating);
  const avgRating = ratingsGiven.length > 0 ? (ratingsGiven.reduce((s, j) => s + (j.rating || 0), 0) / ratingsGiven.length).toFixed(1) : '—';
  const memberSince = new Date().toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  });
  const sections: Array<{
    title: string;
    items: Array<{
      icon: ComponentType<{
        className?: string;
      }>;
      label: string;
      sub?: string;
      tint: string;
      sheet: Exclude<ProfileSheet, null>;
    }>;
  }> = [{
    title: 'Account',
    items: [{
      icon: CreditCard,
      label: 'Payment methods',
      sub: '2 cards · Visa default',
      tint: 'bg-blue-50 text-blue-600',
      sheet: 'payments'
    }, {
      icon: MapPin,
      label: 'Saved addresses',
      sub: 'Home · Office',
      tint: 'bg-emerald-50 text-emerald-600',
      sheet: 'addresses'
    }]
  }, {
    title: 'Preferences',
    items: [{
      icon: Bell,
      label: 'Notifications',
      sub: 'Push, SMS · Email off',
      tint: 'bg-amber-50 text-amber-600',
      sheet: 'notifications'
    }, {
      icon: Shield,
      label: 'Privacy & security',
      sub: 'Face ID enabled',
      tint: 'bg-purple-50 text-purple-600',
      sheet: 'privacy'
    }]
  }, {
    title: 'Support',
    items: [{
      icon: HelpCircle,
      label: 'Help & support',
      sub: 'FAQs · contact us',
      tint: 'bg-rose-50 text-rose-600',
      sheet: 'help'
    }]
  }];
  return <div className="pb-10" data-id="element-1940">
      <div className="relative px-6 pt-6 pb-8 bg-gradient-to-b from-gray-900 to-gray-800 text-white overflow-hidden" data-id="element-1941">
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,107,53,0.6) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,107,53,0.4) 0%, transparent 50%)'
      }} data-id="element-1942" />
        <div className="relative" data-id="element-1943">
          <div className="flex items-start gap-4" data-id="element-1944">
            <div className="relative" data-id="element-1945">
              <div className="w-[68px] h-[68px] rounded-2xl orange-gradient-bg text-white flex items-center justify-center text-2xl font-display font-black shadow-xl ring-4 ring-white/10" data-id="element-1946">
                {initial}
              </div>
              <button onClick={() => setSheet('edit')} className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white text-gray-900 flex items-center justify-center shadow-md ring-2 ring-gray-900 active:scale-95 transition-transform" aria-label="Edit profile" data-id="element-1947">
                <Settings className="w-3.5 h-3.5" data-id="element-1948" />
              </button>
            </div>
            <div className="min-w-0 flex-1 pt-1" data-id="element-1949">
              <h2 className="text-xl font-display font-bold leading-tight truncate" data-id="element-1950">
                {displayName}
              </h2>
              <p className="text-xs text-gray-300 mt-0.5 truncate" data-id="element-1951">{phone}</p>
              <div className="inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm" data-id="element-1952">
                <Sparkles className="w-3 h-3 text-amber-300" data-id="element-1953" />
                <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-1954">
                  Member · since {memberSince}
                </span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-6" data-id="element-1955">
            {[{
            label: 'Total jobs',
            value: String(jobs.length)
          }, {
            label: 'Spent',
            value: `AED ${totalSpent}`
          }, {
            label: 'Avg rating',
            value: avgRating
          }].map((s, i) => <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-2.5 text-center" data-id="element-1956">
                <p className="text-[9px] font-bold uppercase tracking-wider text-gray-300 mb-0.5" data-id="element-1957">
                  {s.label}
                </p>
                <p className="text-sm font-display font-bold tabular-nums truncate" data-id="element-1958">
                  {s.value}
                </p>
              </div>)}
          </div>
        </div>
      </div>
      <div className="px-5 pt-6 space-y-5 relative" data-id="element-1959">
        {sections.map(sec => <div key={sec.title} data-id="element-1960">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400 px-2 mb-2" data-id="element-1961">
              {sec.title}
            </p>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.04)] overflow-hidden divide-y divide-gray-50" data-id="element-1962">
              {sec.items.map((it, i) => <button key={i} onClick={() => setSheet(it.sheet)} className="w-full flex items-center gap-3.5 px-4 py-3.5 hover:bg-gray-50/60 active:bg-gray-100/60 transition-colors text-left" data-id="element-1963">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${it.tint}`} data-id="element-1964">
                    <it.icon className="w-4 h-4" data-id="element-1965" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1966">
                    <p className="text-sm font-semibold text-gray-900 leading-tight" data-id="element-1967">
                      {it.label}
                    </p>
                    {it.sub && <p className="text-[11px] text-gray-500 mt-0.5 truncate" data-id="element-1968">
                        {it.sub}
                      </p>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" data-id="element-1969" />
                </button>)}
            </div>
          </div>)}
        <button onClick={() => setSheet('signout')} className="w-full mt-2 py-3.5 bg-white border border-gray-100 hover:border-rose-200 text-rose-600 rounded-2xl text-sm font-bold shadow-[0_2px_8px_-2px_rgba(0,0,0,0.04)] active:scale-[0.99] transition-all flex items-center justify-center gap-2" data-id="element-1970">
          <LogOut className="w-4 h-4" data-id="element-1971" />
          Sign out
        </button>
        <p className="text-[10px] text-center text-gray-400 pt-2" data-id="element-1972">
          Pair2 · v1.0.0
        </p>
      </div>
      {sheet === 'edit' && <EditProfileSheet displayName={displayName} phone={phone} onClose={() => setSheet(null)} data-id="element-1973" />}
      {sheet === 'payments' && <PaymentMethodsSheet onClose={() => setSheet(null)} data-id="element-1974" />}
      {sheet === 'addresses' && <AddressesSheet onClose={() => setSheet(null)} data-id="element-1975" />}
      {sheet === 'notifications' && <NotificationsSheet onClose={() => setSheet(null)} data-id="element-1976" />}
      {sheet === 'help' && <HelpSheet onClose={() => setSheet(null)} data-id="element-1977" />}
      {sheet === 'privacy' && <PrivacySheet onClose={() => setSheet(null)} data-id="element-1978" />}
      {sheet === 'signout' && <SignOutSheet displayName={displayName} onCancel={() => setSheet(null)} onConfirm={() => {
      setSheet(null);
      onSignOut();
    }} data-id="element-1979" />}
    </div>;
}
function SheetHeader({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) {
  return <div className="px-6 pb-3" data-id="element-1980">
      <h3 className="text-lg font-display font-bold text-gray-900" data-id="element-1981">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5" data-id="element-1982">{subtitle}</p>}
    </div>;
}
function EditProfileSheet({
  displayName,
  phone,
  onClose
}: {
  displayName: string;
  phone: string;
  onClose: () => void;
}) {
  const [name, setName] = useState(displayName);
  const [email, setEmail] = useState('');
  return <BottomSheet onClose={onClose} data-id="element-1983">
      <SheetHeader title="Edit profile" subtitle="Update your account details" data-id="element-1984" />
      <div className="px-6 pb-6 space-y-3" data-id="element-1985">
        <div data-id="element-1986">
          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5 block" data-id="element-1987">
            Full name
          </label>
          <input value={name} onChange={e => setName(e.target.value)} className="w-full px-3.5 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:border-gray-900 transition-colors" data-id="element-1988" />
        </div>
        <div data-id="element-1989">
          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5 block" data-id="element-1990">
            Mobile number
          </label>
          <input value={phone} disabled className="w-full px-3.5 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-400" data-id="element-1991" />
          <p className="text-[10px] text-gray-400 mt-1" data-id="element-1992">
            Mobile number is verified and cannot be changed here.
          </p>
        </div>
        <div data-id="element-1993">
          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5 block" data-id="element-1994">
            Email (optional)
          </label>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" type="email" className="w-full px-3.5 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:border-gray-900 transition-colors" data-id="element-1995" />
        </div>
        <button onClick={onClose} className="w-full mt-2 py-3.5 bg-gray-900 text-white rounded-xl font-bold text-sm active:scale-[0.98] transition-transform" data-id="element-1996">
          Save changes
        </button>
      </div>
    </BottomSheet>;
}
function PaymentMethodsSheet({
  onClose
}: {
  onClose: () => void;
}) {
  const [methods, setMethods] = useState([{
    id: '1',
    brand: 'VISA',
    label: 'Visa •••• 4242',
    sub: 'Expires 09/27',
    isDefault: true
  }, {
    id: '2',
    brand: 'MC',
    label: 'Mastercard •••• 8821',
    sub: 'Expires 12/26',
    isDefault: false
  }]);
  const setDefault = (id: string) => setMethods(m => m.map(x => ({
    ...x,
    isDefault: x.id === id
  })));
  return <BottomSheet onClose={onClose} data-id="element-1997">
      <SheetHeader title="Payment methods" subtitle="Cards used for bookings and bidding" data-id="element-1998" />
      <div className="px-6 pb-6 space-y-2.5" data-id="element-1999">
        {methods.map(m => <div key={m.id} className={`p-3.5 rounded-2xl border-2 transition-all ${m.isDefault ? 'border-gray-900 bg-white' : 'border-gray-100 bg-white'}`} data-id="element-2000">
            <div className="flex items-center gap-3" data-id="element-2001">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700 flex items-center justify-center font-black text-[11px] shrink-0" data-id="element-2002">
                {m.brand}
              </div>
              <div className="flex-1 min-w-0" data-id="element-2003">
                <p className="text-sm font-bold text-gray-900 leading-tight" data-id="element-2004">
                  {m.label}
                </p>
                <p className="text-[11px] text-gray-500" data-id="element-2005">{m.sub}</p>
              </div>
              {m.isDefault ? <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-gray-900 text-white uppercase tracking-wider" data-id="element-2006">
                  Default
                </span> : <button onClick={() => setDefault(m.id)} className="text-[11px] font-bold text-gray-700 hover:text-gray-900 px-2 py-1" data-id="element-2007">
                  Set default
                </button>}
            </div>
          </div>)}
        <button className="w-full py-3.5 mt-2 border-2 border-dashed border-gray-200 hover:border-gray-300 text-gray-700 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors" data-id="element-2008">
          <Plus className="w-4 h-4" data-id="element-2009" />
          Add new card
        </button>
      </div>
    </BottomSheet>;
}
function AddressesSheet({
  onClose
}: {
  onClose: () => void;
}) {
  const addresses = [{
    id: '1',
    label: 'Home',
    icon: HomeIcon,
    line: 'Tower 12, Marina Heights, Dubai Marina',
    tag: 'Default pickup'
  }, {
    id: '2',
    label: 'Office',
    icon: Briefcase,
    line: 'DIFC Gate Village 8, Level 4',
    tag: 'Work'
  }];
  return <BottomSheet onClose={onClose} data-id="element-2010">
      <SheetHeader title="Saved addresses" subtitle="Faster booking with quick-pick locations" data-id="element-2011" />
      <div className="px-6 pb-6 space-y-2.5" data-id="element-2012">
        {addresses.map(a => <div key={a.id} className="p-3.5 rounded-2xl border border-gray-100 bg-white" data-id="element-2013">
            <div className="flex items-start gap-3" data-id="element-2014">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-2015">
                <a.icon className="w-4 h-4" data-id="element-2016" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2017">
                <div className="flex items-center gap-2 mb-0.5" data-id="element-2018">
                  <p className="text-sm font-bold text-gray-900 leading-tight" data-id="element-2019">
                    {a.label}
                  </p>
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" data-id="element-2020">
                    {a.tag}
                  </span>
                </div>
                <p className="text-xs text-gray-500 leading-snug" data-id="element-2021">{a.line}</p>
              </div>
            </div>
          </div>)}
        <button className="w-full py-3.5 mt-2 border-2 border-dashed border-gray-200 hover:border-gray-300 text-gray-700 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors" data-id="element-2022">
          <Plus className="w-4 h-4" data-id="element-2023" />
          Add new address
        </button>
      </div>
    </BottomSheet>;
}
function NotificationsSheet({
  onClose
}: {
  onClose: () => void;
}) {
  const [prefs, setPrefs] = useState({
    push: true,
    sms: true,
    email: false,
    bidUpdates: true,
    promos: false
  });
  const toggle = (k: keyof typeof prefs) => setPrefs(p => ({
    ...p,
    [k]: !p[k]
  }));
  const rows: Array<{
    key: keyof typeof prefs;
    label: string;
    sub: string;
  }> = [{
    key: 'push',
    label: 'Push notifications',
    sub: 'Job updates and chat replies'
  }, {
    key: 'sms',
    label: 'SMS',
    sub: 'Driver arrival and OTP codes'
  }, {
    key: 'email',
    label: 'Email',
    sub: 'Receipts and summaries'
  }, {
    key: 'bidUpdates',
    label: 'Bid updates',
    sub: 'When carriers submit a new bid'
  }, {
    key: 'promos',
    label: 'Promotions',
    sub: 'Occasional offers and credits'
  }];
  return <BottomSheet onClose={onClose} data-id="element-2024">
      <SheetHeader title="Notifications" subtitle="Choose what to hear from Pair2" data-id="element-2025" />
      <div className="px-6 pb-6 space-y-1.5" data-id="element-2026">
        {rows.map(r => {
        const on = prefs[r.key];
        return <button key={r.key} onClick={() => toggle(r.key)} className="w-full flex items-center gap-3 p-3.5 rounded-2xl hover:bg-gray-50 transition-colors text-left" data-id="element-2027">
              <div className="flex-1 min-w-0" data-id="element-2028">
                <p className="text-sm font-semibold text-gray-900 leading-tight" data-id="element-2029">
                  {r.label}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-2030">{r.sub}</p>
              </div>
              <span className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${on ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-2031">
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-2032" />
              </span>
            </button>;
      })}
      </div>
    </BottomSheet>;
}
function HelpSheet({
  onClose
}: {
  onClose: () => void;
}) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const faqs = [{
    q: 'How does bidding work?',
    a: "Open a job for bidding and carriers compete on price for ~3 minutes. You'll see live bids and can accept the best one."
  }, {
    q: 'When am I charged?',
    a: 'We hold the funds when you book. The carrier is paid only after you confirm delivery.'
  }, {
    q: 'Can I cancel a job?',
    a: "Yes — you can cancel any job that hasn't started. A small fee may apply if the driver is already en route."
  }, {
    q: 'How do refunds work?',
    a: 'Cancellations before pickup are refunded in full. Disputes are reviewed within 24 hours.'
  }];
  return <BottomSheet onClose={onClose} data-id="element-2033">
      <SheetHeader title="Help & support" subtitle="We're here 24/7" data-id="element-2034" />
      <div className="px-6 pb-6 space-y-3" data-id="element-2035">
        <div className="grid grid-cols-2 gap-2" data-id="element-2036">
          <button className="p-3 rounded-2xl bg-gray-900 text-white text-left active:scale-[0.98] transition-transform" data-id="element-2037">
            <Phone className="w-4 h-4 mb-1.5" data-id="element-2038" />
            <p className="text-xs font-bold" data-id="element-2039">Call support</p>
            <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-2040">24/7 hotline</p>
          </button>
          <button className="p-3 rounded-2xl bg-white border border-gray-200 text-gray-900 text-left active:scale-[0.98] transition-transform" data-id="element-2041">
            <MessageCircle className="w-4 h-4 mb-1.5" data-id="element-2042" />
            <p className="text-xs font-bold" data-id="element-2043">Live chat</p>
            <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-2044">~ 2 min reply</p>
          </button>
        </div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 px-1 pt-2" data-id="element-2045">
          Frequently asked
        </p>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden divide-y divide-gray-50" data-id="element-2046">
          {faqs.map((f, i) => {
          const open = openIdx === i;
          return <button key={i} onClick={() => setOpenIdx(open ? null : i)} className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors" data-id="element-2047">
                <div className="flex items-start justify-between gap-3" data-id="element-2048">
                  <p className="text-sm font-semibold text-gray-900 leading-snug" data-id="element-2049">
                    {f.q}
                  </p>
                  <ChevronRight className={`w-4 h-4 text-gray-400 shrink-0 mt-0.5 transition-transform ${open ? 'rotate-90' : ''}`} data-id="element-2050" />
                </div>
                {open && <p className="text-xs text-gray-600 mt-2 leading-relaxed" data-id="element-2051">
                    {f.a}
                  </p>}
              </button>;
        })}
        </div>
      </div>
    </BottomSheet>;
}
function PrivacySheet({
  onClose
}: {
  onClose: () => void;
}) {
  const [prefs, setPrefs] = useState({
    faceId: true,
    twoFa: false,
    shareData: false,
    locationOnly: true
  });
  const toggle = (k: keyof typeof prefs) => setPrefs(p => ({
    ...p,
    [k]: !p[k]
  }));
  const rows: Array<{
    key: keyof typeof prefs;
    icon: ComponentType<{
      className?: string;
    }>;
    label: string;
    sub: string;
  }> = [{
    key: 'faceId',
    icon: Shield,
    label: 'Face ID for payments',
    sub: 'Confirm purchases with biometrics'
  }, {
    key: 'twoFa',
    icon: Shield,
    label: 'Two-factor authentication',
    sub: 'Extra layer of account security'
  }, {
    key: 'locationOnly',
    icon: MapPin,
    label: 'Location while using app',
    sub: 'Only share location during active jobs'
  }, {
    key: 'shareData',
    icon: Sparkles,
    label: 'Personalized recommendations',
    sub: 'Use your activity to improve suggestions'
  }];
  return <BottomSheet onClose={onClose} data-id="element-2052">
      <SheetHeader title="Privacy & security" subtitle="Control your data and account safety" data-id="element-2053" />
      <div className="px-6 pb-6 space-y-1.5" data-id="element-2054">
        {rows.map(r => {
        const on = prefs[r.key];
        return <button key={r.key} onClick={() => toggle(r.key)} className="w-full flex items-center gap-3 p-3.5 rounded-2xl hover:bg-gray-50 transition-colors text-left" data-id="element-2055">
              <div className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0" data-id="element-2056">
                <r.icon className="w-4 h-4" data-id="element-2057" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2058">
                <p className="text-sm font-semibold text-gray-900 leading-tight" data-id="element-2059">
                  {r.label}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-2060">{r.sub}</p>
              </div>
              <span className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${on ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-2061">
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-2062" />
              </span>
            </button>;
      })}
        <button className="w-full mt-3 py-3 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-xl transition-colors" data-id="element-2063">
          Delete account
        </button>
      </div>
    </BottomSheet>;
}
function SignOutSheet({
  displayName,
  onCancel,
  onConfirm
}: {
  displayName: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return <BottomSheet onClose={onCancel} data-id="element-2064">
      <div className="px-6 pb-6 pt-2 text-center" data-id="element-2065">
        <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-4" data-id="element-2066">
          <LogOut className="w-6 h-6" data-id="element-2067" />
        </div>
        <h3 className="text-lg font-display font-bold text-gray-900 mb-1" data-id="element-2068">
          Sign out?
        </h3>
        <p className="text-sm text-gray-500 mb-6 max-w-[260px] mx-auto leading-relaxed" data-id="element-2069">
          You'll be signed out of{' '}
          <span className="font-semibold text-gray-700" data-id="element-2070">{displayName}</span>
          's account. Active jobs will keep running, but you'll need to sign
          back in to manage them.
        </p>
        <div className="space-y-2" data-id="element-2071">
          <button onClick={onConfirm} className="w-full py-3.5 bg-rose-500 hover:bg-rose-600 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all" data-id="element-2072">
            <LogOut className="w-4 h-4" data-id="element-2073" />
            Yes, sign me out
          </button>
          <button onClick={onCancel} className="w-full py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl text-sm font-bold transition-colors" data-id="element-2074">
            Cancel
          </button>
        </div>
      </div>
    </BottomSheet>;
}
function HomeTab({
  jobs,
  onStartChat
}: {
  jobs: MobileJobRecord[];
  onStartChat: (serviceId?: ServiceId, prompt?: string) => void;
}) {
  const active = jobs.find(t => t.status === 'active');
  return <div className="px-6 pt-4 pb-8 space-y-6" data-id="element-2075">
      <div className="flex items-center justify-between" data-id="element-2076">
        <div data-id="element-2077">
          <p className="text-xs text-gray-500 font-medium" data-id="element-2078">Good morning</p>
          <h1 className="text-2xl font-display font-bold text-gray-900 leading-tight" data-id="element-2079">
            Hi <span className="text-brand-500" data-id="element-2080">Sarah</span>
          </h1>
        </div>
        <button className="relative w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-2081">
          <Bell className="w-5 h-5 text-gray-700" data-id="element-2082" />
          <span className="absolute top-2 right-2 w-2 h-2 bg-brand-500 rounded-full" data-id="element-2083" />
        </button>
      </div>

      <FuturisticAiPrompt onStartChat={() => onStartChat()} data-id="element-2084" />

      {active && <div className="bg-brand-500 rounded-3xl p-5 text-white shadow-lg shadow-brand-500/20 relative overflow-hidden" data-id="element-2085">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-2086" />
          <div className="relative z-10" data-id="element-2087">
            <div className="flex items-center justify-between mb-3" data-id="element-2088">
              <span className="text-[10px] font-bold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full" data-id="element-2089">
                In progress
              </span>
              <span className="text-[10px] font-bold text-white/80" data-id="element-2090">
                {active.id}
              </span>
            </div>
            <h3 className="font-bold text-lg mb-1" data-id="element-2091">{active.serviceName}</h3>
            <p className="text-xs text-white/80 flex items-center gap-1.5 mb-4" data-id="element-2092">
              <MapPin className="w-3.5 h-3.5" data-id="element-2093" /> {active.dropoff}
            </p>
            <div className="flex items-center justify-between pt-3 border-t border-white/20" data-id="element-2094">
              <div className="flex items-center gap-2" data-id="element-2095">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold" data-id="element-2096">
                  {active.supplierName?.charAt(0) || 'D'}
                </div>
                <span className="text-xs font-medium" data-id="element-2097">
                  {active.supplierName || 'Driver'}
                </span>
              </div>
              <button className="text-[10px] font-bold bg-white text-brand-600 px-3 py-1.5 rounded-full" data-id="element-2098">
                Track
              </button>
            </div>
          </div>
        </div>}

      <div data-id="element-2099">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3" data-id="element-2100">
          Services
        </h2>
        <div className="grid grid-cols-3 gap-2" data-id="element-2101">
          {SERVICES.map(s => {
          const Icon = s.icon;
          return <button key={s.id} onClick={() => onStartChat(s.id as ServiceId)} className="bg-white border border-gray-100 rounded-2xl p-3 shadow-sm hover:shadow-md hover:border-gray-200 transition-all text-left" data-id="element-2102">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${COLOR_MAP[s.color]}`} data-id="element-2103">
                  <Icon className="w-4 h-4" data-id="element-2104" />
                </div>
                <p className="text-[11px] font-bold text-gray-900 leading-tight" data-id="element-2105">
                  {s.label}
                </p>
              </button>;
        })}
        </div>
      </div>

      <div className="bg-gradient-to-br from-brand-50 to-purple-50 border border-brand-100 rounded-2xl p-4 flex items-start gap-3" data-id="element-2106">
        <div className="w-8 h-8 rounded-lg bg-white text-brand-600 flex items-center justify-center shrink-0" data-id="element-2107">
          <Sparkles className="w-4 h-4" data-id="element-2108" />
        </div>
        <div data-id="element-2109">
          <p className="text-sm font-bold text-gray-900 leading-snug" data-id="element-2110">
            Open bidding saves you 18% on average.
          </p>
          <p className="text-xs text-gray-600 mt-0.5" data-id="element-2111">
            Let multiple carriers compete for your next move.
          </p>
        </div>
      </div>
    </div>;
}
function FuturisticAiPrompt({
  onStartChat
}: {
  onStartChat: () => void;
}) {
  const ROTATING = ['What do you want to move?', 'What service do you need?', 'Need a recovery truck?', 'Moving home or furniture?', 'Got cargo to transport?'];
  const [phraseIdx, setPhraseIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhraseIdx(i => (i + 1) % ROTATING.length), 2400);
    return () => clearInterval(t);
  }, []);
  return <button onClick={onStartChat} className="relative w-full text-left group" aria-label="Open AI assistant" data-id="element-2112">
      <motion.div animate={{
      background: ['linear-gradient(90deg, rgba(255,107,53,0.3), rgba(168,85,247,0.25), rgba(59,130,246,0.25), rgba(255,107,53,0.3))', 'linear-gradient(270deg, rgba(255,107,53,0.3), rgba(168,85,247,0.25), rgba(59,130,246,0.25), rgba(255,107,53,0.3))']
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute -inset-[1.5px] rounded-[1.4rem] blur-md opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-2113" />
      <div className="relative bg-white rounded-[1.3rem] p-4 flex items-center gap-3 border border-white shadow-lg shadow-gray-200/60 overflow-hidden" data-id="element-2114">
        <motion.div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-brand-400/10 blur-2xl" animate={{
        scale: [1, 1.3, 1],
        opacity: [0.5, 0.9, 0.5]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2115" />
        <div className="relative shrink-0" data-id="element-2116">
          <motion.div animate={{
          boxShadow: ['0 0 0 0 rgba(255,107,53,0.4)', '0 0 0 8px rgba(255,107,53,0)']
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeOut'
        }} className="absolute inset-0 rounded-xl" data-id="element-2117" />
          <div className="relative w-11 h-11 rounded-xl orange-gradient-bg flex items-center justify-center" data-id="element-2118">
            <Sparkles className="w-5 h-5 text-white" data-id="element-2119" />
          </div>
        </div>
        <div className="flex-1 min-w-0 relative h-[40px] flex flex-col justify-center" data-id="element-2120">
          <div className="flex items-center gap-1.5 mb-0.5" data-id="element-2121">
            <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-brand-600" data-id="element-2122">
              Pair2 AI
            </span>
            <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" data-id="element-2123" />
            <span className="text-[9px] font-medium text-gray-400" data-id="element-2124">
              ready to help
            </span>
          </div>
          <div className="relative h-5 overflow-hidden" data-id="element-2125">
            <AnimatePresence mode="wait" data-id="element-2126">
              <motion.p key={phraseIdx} initial={{
              y: 16,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} exit={{
              y: -16,
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} className="absolute inset-0 text-[13px] text-gray-900 font-semibold truncate" data-id="element-2127">
                {ROTATING[phraseIdx]}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
        <Mic className="w-5 h-5 text-gray-400 group-hover:text-brand-500 transition-colors shrink-0" data-id="element-2128" />
      </div>
    </button>;
}
function ActiveAction({
  icon: Icon,
  label
}: {
  icon: typeof Phone;
  label: string;
}) {
  return <button className="flex flex-col items-center gap-1 py-2 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors" data-id="element-2129">
      <Icon className="w-4 h-4 text-white" data-id="element-2130" />
      <span className="text-[10px] font-bold text-white" data-id="element-2131">{label}</span>
    </button>;
}
type PrimaryFilter = 'all' | 'bidding';
type SubFilter = 'any' | 'active' | 'completed' | 'cancelled';
function TripsTab({
  jobs,
  setJobs,
  onStartChat
}: {
  jobs: MobileJobRecord[];
  setJobs: React.Dispatch<React.SetStateAction<MobileJobRecord[]>>;
  onStartChat: () => void;
}) {
  const [filter, setFilter] = useState<PrimaryFilter>('all');
  const [subFilter, setSubFilter] = useState<SubFilter>('any');
  const [rateTarget, setRateTarget] = useState<MobileJobRecord | null>(null);
  const [receiptTarget, setReceiptTarget] = useState<MobileJobRecord | null>(null);
  const [trackingTarget, setTrackingTarget] = useState<MobileJobRecord | null>(null);
  const [bidsTarget, setBidsTarget] = useState<MobileJobRecord | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MobileJobRecord | null>(null);
  const [payTarget, setPayTarget] = useState<MobileJobRecord | null>(null);
  const unratedJob = jobs.filter(j => j.status === 'completed' && !j.rating).sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))[0];
  const filteredJobs = jobs.filter(j => {
    // Primary filter
    if (filter === 'bidding' && j.type !== 'bidding') return false;
    // Sub filter (only meaningful when primary === 'all')
    if (filter === 'all') {
      if (subFilter === 'active') return j.status === 'active' || j.status === 'pending_payment';
      if (subFilter === 'completed') return j.status === 'completed';
      if (subFilter === 'cancelled') return j.status === 'cancelled';
    }
    return true;
  }).sort((a, b) => b.createdAt - a.createdAt);
  const counts = {
    all: jobs.length,
    active: jobs.filter(j => j.status === 'active' || j.status === 'pending_payment').length,
    bidding: jobs.filter(j => j.type === 'bidding').length,
    completed: jobs.filter(j => j.status === 'completed').length,
    cancelled: jobs.filter(j => j.status === 'cancelled').length
  };
  const handleRateSubmit = (jobId: string, stars: number, review: string, tags: string[]) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      rating: stars,
      reviewText: review,
      ratingTags: tags,
      ratedAt: Date.now()
    } : j));
  };
  const handleCancelJob = (jobId: string) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      status: 'cancelled',
      cancelledAt: Date.now(),
      bids: j.bids?.map(b => !b.status || b.status === 'pending' ? {
        ...b,
        status: 'cancelled'
      } : b)
    } : j));
  };
  const handlePaymentComplete = (jobId: string) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      status: 'active',
      startedAt: Date.now()
    } : j));
  };
  const handleAcceptBid = (jobId: string, bidIndex: number) => {
    setJobs(prev => prev.map(j => {
      if (j.id !== jobId || !j.bids) return j;
      const accepted = j.bids[bidIndex];
      return {
        ...j,
        status: 'pending_payment',
        supplierName: accepted.supplier,
        supplierTruck: accepted.truck || 'Carrier-selected truck',
        price: accepted.price,
        bids: j.bids.map((b, i) => ({
          ...b,
          status: i === bidIndex ? 'accepted' : !b.status || b.status === 'pending' ? 'rejected' : b.status
        }))
      };
    }));
  };
  const handleRejectBid = (jobId: string, bidIndex: number) => {
    setJobs(prev => prev.map(j => {
      if (j.id !== jobId || !j.bids) return j;
      return {
        ...j,
        bids: j.bids.map((b, i) => ({
          ...b,
          status: i === bidIndex && (!b.status || b.status === 'pending') ? 'rejected' : b.status
        }))
      };
    }));
  };
  return <div className="flex flex-col h-full bg-gray-50" data-id="element-2132">
      <div className="px-6 pt-6 pb-4 bg-white border-b border-gray-100 shrink-0" data-id="element-2133">
        <div className="flex items-center justify-between mb-4" data-id="element-2134">
          <h1 className="text-2xl font-display font-bold text-gray-900 tracking-tight" data-id="element-2135">
            My Jobs
          </h1>
          <button onClick={onStartChat} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-full text-xs font-bold" data-id="element-2136">
            <Plus className="w-3.5 h-3.5" data-id="element-2137" />
            New
          </button>
        </div>

        {/* Primary segmented control */}
        <div className="relative bg-gray-100/80 rounded-2xl p-1 flex" data-id="element-2138">
          {([{
          id: 'all',
          label: 'All',
          count: counts.all
        }, {
          id: 'bidding',
          label: 'Bidding',
          count: counts.bidding
        }] as const).map(f => {
          const isActive = filter === f.id;
          return <button key={f.id} onClick={() => setFilter(f.id)} className="relative flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 focus:outline-none" aria-pressed={isActive} data-id="element-2139">
                {isActive && <motion.div layoutId="jobs-primary-seg" className="absolute inset-0 bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.08)] ring-1 ring-black/5" transition={{
              type: 'spring',
              stiffness: 380,
              damping: 32
            }} data-id="element-2140" />}
                <span className={`relative z-10 transition-colors ${isActive ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-2141">
                  {f.label}
                </span>
                <span className={`relative z-10 text-[11px] font-bold tabular-nums transition-colors ${isActive ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-2142">
                  {f.count}
                </span>
              </button>;
        })}
        </div>

        {/* Sub-filter chips — only when primary === 'all' */}
        <AnimatePresence initial={false} data-id="element-2143">
          {filter === 'all' && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: 'easeOut'
        }} className="overflow-hidden" data-id="element-2144">
              <div className="flex gap-2 pt-3 overflow-x-auto -mx-2 px-2 scrollbar-hide" data-id="element-2145">
                {([{
              id: 'any',
              label: 'Everything',
              dot: 'bg-gray-300',
              count: counts.all
            }, {
              id: 'active',
              label: 'Active',
              dot: 'bg-amber-500',
              count: counts.active
            }, {
              id: 'completed',
              label: 'Completed',
              dot: 'bg-emerald-500',
              count: counts.completed
            }, {
              id: 'cancelled',
              label: 'Cancelled',
              dot: 'bg-gray-400',
              count: counts.cancelled
            }] as const).map(s => {
              const isActive = subFilter === s.id;
              return <button key={s.id} onClick={() => setSubFilter(s.id)} className={`relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-all shrink-0 ${isActive ? 'bg-gray-900 text-white ring-1 ring-gray-900' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:ring-gray-300'}`} aria-pressed={isActive} data-id="element-2146">
                      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-white/70' : s.dot}`} data-id="element-2147" />
                      {s.label}
                      <span className={`text-[10px] tabular-nums ${isActive ? 'text-white/60' : 'text-gray-400'}`} data-id="element-2148">
                        {s.count}
                      </span>
                    </button>;
            })}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-id="element-2149">
        {unratedJob && <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between shadow-sm" data-id="element-2150">
            <div className="flex items-center gap-3" data-id="element-2151">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600" data-id="element-2152">
                <Star className="w-5 h-5 fill-current" data-id="element-2153" />
              </div>
              <div data-id="element-2154">
                <p className="text-xs font-bold text-amber-900 uppercase tracking-wider mb-0.5" data-id="element-2155">
                  Rate your trip
                </p>
                <p className="text-sm font-semibold text-amber-800" data-id="element-2156">
                  {unratedJob.supplierName}
                </p>
              </div>
            </div>
            <button onClick={() => setRateTarget(unratedJob)} className="px-4 py-2 bg-amber-500 text-white rounded-xl text-xs font-bold shadow-sm" data-id="element-2157">
              Rate now
            </button>
          </div>}

        <AnimatePresence mode="popLayout" data-id="element-2158">
          {filteredJobs.length === 0 ? <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="bg-white border border-gray-100 rounded-3xl p-8 text-center mt-4" data-id="element-2159">
              <div className="w-12 h-12 rounded-2xl bg-gray-50 text-gray-400 mx-auto mb-3 flex items-center justify-center" data-id="element-2160">
                <ListChecks className="w-6 h-6" data-id="element-2161" />
              </div>
              <p className="text-sm font-bold text-gray-900" data-id="element-2162">
                No {filter !== 'all' ? filter : ''} jobs found
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-2163">
                When you book or request a service, it will appear here.
              </p>
            </motion.div> : filteredJobs.map((job, i) => <motion.div key={job.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          delay: i * 0.04
        }} data-id="element-2164">
                <MobileJobCard job={job} onRate={() => setRateTarget(job)} onReceipt={() => setReceiptTarget(job)} onTrack={() => setTrackingTarget(job)} onBids={() => setBidsTarget(job)} onCancel={() => setCancelTarget(job)} onPay={() => setPayTarget(job)} data-id="element-2165" />
              </motion.div>)}
        </AnimatePresence>
      </div>

      <AnimatePresence data-id="element-2166">
        {rateTarget && <RateSheet job={rateTarget} onClose={() => setRateTarget(null)} onSubmit={handleRateSubmit} data-id="element-2167" />}
        {receiptTarget && <ReceiptSheet job={receiptTarget} onClose={() => setReceiptTarget(null)} data-id="element-2168" />}
        {trackingTarget && <TrackingSheet job={trackingTarget} onClose={() => setTrackingTarget(null)} onCancel={() => {
        setTrackingTarget(null);
        setCancelTarget(trackingTarget);
      }} data-id="element-2169" />}
        {bidsTarget && <BidsSheet job={bidsTarget} onClose={() => setBidsTarget(null)} onAccept={handleAcceptBid} onReject={handleRejectBid} onCancel={() => {
        setBidsTarget(null);
        setCancelTarget(bidsTarget);
      }} data-id="element-2170" />}
        {payTarget && <PaySheet job={payTarget} onClose={() => setPayTarget(null)} onPaid={() => {
        handlePaymentComplete(payTarget.id);
        setPayTarget(null);
      }} data-id="element-2171" />}
        {cancelTarget && <CancelSheet job={cancelTarget} onClose={() => setCancelTarget(null)} onConfirm={() => {
        handleCancelJob(cancelTarget.id);
        setCancelTarget(null);
      }} data-id="element-2172" />}
      </AnimatePresence>
    </div>;
}
function MobileJobCard({
  job,
  onRate,
  onReceipt,
  onTrack,
  onBids,
  onCancel,
  onPay
}: {
  job: MobileJobRecord;
  onRate: () => void;
  onReceipt: () => void;
  onTrack: () => void;
  onBids: () => void;
  onCancel: () => void;
  onPay: () => void;
}) {
  const Icon = SERVICE_ICON_MAP[job.serviceId] || Package;
  const colorClass = SERVICE_COLOR_MAP[job.serviceId] || 'bg-gray-100 text-gray-600';
  const pillMap: Record<MobileJobStatus, string> = {
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    active: 'bg-amber-50 text-amber-700 border-amber-200',
    pending_payment: 'bg-blue-50 text-blue-700 border-blue-200',
    bidding_open: 'bg-brand-50 text-brand-700 border-brand-200',
    cancelled: 'bg-gray-50 text-gray-600 border-gray-200'
  };
  const pillLabel: Record<MobileJobStatus, string> = {
    completed: 'Completed',
    active: 'In progress',
    pending_payment: 'Pending payment',
    bidding_open: 'Bidding open',
    cancelled: 'Cancelled'
  };
  const created = new Date(job.createdAt);
  const isBiddingOpen = job.status === 'bidding_open';
  const closesIn = isBiddingOpen && job.closesAt ? Math.max(0, job.closesAt - Date.now()) : 0;
  const hoursLeft = Math.floor(closesIn / (60 * 60 * 1000));
  const pendingBids = job.bids?.filter(b => !b.status || b.status === 'pending') || [];
  const bestBid = pendingBids.length > 0 ? Math.min(...pendingBids.map(b => b.price)) : null;
  return <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-2173">
      <div className="p-4" data-id="element-2174">
        <div className="flex items-start justify-between mb-3" data-id="element-2175">
          <div className="flex items-center gap-3" data-id="element-2176">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${colorClass}`} data-id="element-2177">
              <Icon className="w-5 h-5" data-id="element-2178" />
            </div>
            <div data-id="element-2179">
              <div className="flex items-center gap-2 mb-0.5" data-id="element-2180">
                <h3 className="font-bold text-gray-900 text-sm" data-id="element-2181">
                  {job.serviceName}
                </h3>
                {job.type === 'bidding' && <span className="text-[9px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded" data-id="element-2182">
                    Bidding
                  </span>}
              </div>
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${pillMap[job.status]}`} data-id="element-2183">
                {job.status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" data-id="element-2184" />}
                {pillLabel[job.status]}
              </span>
            </div>
          </div>
          <div className="text-right" data-id="element-2185">
            {job.price ? <p className="font-display font-bold text-gray-900" data-id="element-2186">
                AED {job.price}
              </p> : job.estimatedPrice ? <p className="text-xs text-gray-500" data-id="element-2187">
                Est. AED {job.estimatedPrice}
              </p> : null}
            <p className="text-[10px] text-gray-400 font-medium" data-id="element-2188">
              {isBiddingOpen && hoursLeft > 0 ? `Closes in ${hoursLeft}h` : created.toLocaleDateString('en-AE', {
              month: 'short',
              day: 'numeric'
            })}
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-3 mb-3 space-y-2" data-id="element-2189">
          {job.pickup && <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-2190">
              <MapPin className="w-3.5 h-3.5 text-brand-500 shrink-0" data-id="element-2191" />
              <span className="truncate" data-id="element-2192">{job.pickup}</span>
            </div>}
          {job.dropoff && <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-2193">
              <MapPin className="w-3.5 h-3.5 text-emerald-500 shrink-0" data-id="element-2194" />
              <span className="truncate" data-id="element-2195">{job.dropoff}</span>
            </div>}
        </div>

        {job.supplierName && <div className="flex items-center gap-2 text-xs text-gray-500 mb-3 px-1" data-id="element-2196">
            <Truck className="w-3.5 h-3.5" data-id="element-2197" />
            <span className="font-medium text-gray-700" data-id="element-2198">
              {job.supplierName}
            </span>
            {job.supplierTruck && <span data-id="element-2199">• {job.supplierTruck}</span>}
          </div>}

        {isBiddingOpen && <div className="flex items-center justify-between bg-brand-50/50 border border-brand-100 rounded-xl p-2.5 mb-3" data-id="element-2200">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-brand-700" data-id="element-2201">
              <Gavel className="w-3.5 h-3.5" data-id="element-2202" />
              {pendingBids.length} live bids
            </div>
            {bestBid && <div className="text-xs text-brand-700" data-id="element-2203">
                Best: <span className="font-bold" data-id="element-2204">AED {bestBid}</span>
              </div>}
          </div>}

        {job.rating && <div className="flex items-center gap-1 mb-3 px-1" data-id="element-2205">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= job.rating! ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} data-id="element-2206" />)}
          </div>}

        <div className="flex gap-2 pt-3 border-t border-gray-100" data-id="element-2207">
          {job.status === 'active' && <>
              <button onClick={onCancel} className="px-4 py-2.5 text-xs font-bold text-gray-500 hover:bg-gray-50 rounded-xl" data-id="element-2208">
                Cancel
              </button>
              <button onClick={onTrack} className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5" data-id="element-2209">
                <NavigationIcon className="w-3.5 h-3.5" data-id="element-2210" /> Track live
              </button>
            </>}
          {job.status === 'pending_payment' && <>
              <button onClick={onCancel} className="px-4 py-2.5 text-xs font-bold text-gray-500 hover:bg-gray-50 rounded-xl" data-id="element-2211">
                Cancel
              </button>
              <button onClick={onPay} className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform" data-id="element-2212">
                <CreditCard className="w-3.5 h-3.5" data-id="element-2213" /> Pay now
              </button>
            </>}
          {job.status === 'completed' && !job.rating && <>
              <button onClick={onReceipt} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-xs font-bold" data-id="element-2214">
                View receipt
              </button>
              <button onClick={onRate} className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5" data-id="element-2215">
                <Star className="w-3.5 h-3.5" data-id="element-2216" /> Rate
              </button>
            </>}
          {job.status === 'completed' && job.rating && <button onClick={onReceipt} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5" data-id="element-2217">
              <Receipt className="w-3.5 h-3.5" data-id="element-2218" /> View receipt
            </button>}
          {job.status === 'bidding_open' && <>
              <button onClick={onCancel} className="px-4 py-2.5 text-xs font-bold text-gray-500 hover:bg-gray-50 rounded-xl" data-id="element-2219">
                Cancel
              </button>
              <button onClick={onBids} className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5" data-id="element-2220">
                <Gavel className="w-3.5 h-3.5" data-id="element-2221" /> View bids
              </button>
            </>}
          {job.status === 'cancelled' && <button className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-xs font-bold" data-id="element-2222">
              Book again
            </button>}
        </div>
      </div>
    </div>;
}
function BottomSheet({
  children,
  onClose
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return <div className="absolute inset-0 z-50 flex flex-col justify-end" data-id="element-2223">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-2224" />
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="relative bg-white rounded-t-3xl shadow-2xl max-h-[88%] flex flex-col overflow-hidden" data-id="element-2225">
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-gray-200 rounded-full" data-id="element-2226" />
        <div className="pt-8 flex-1 overflow-y-auto pb-safe" data-id="element-2227">{children}</div>
      </motion.div>
    </div>;
}
function RateSheet({
  job,
  onClose,
  onSubmit
}: {
  job: MobileJobRecord;
  onClose: () => void;
  onSubmit: (id: string, stars: number, review: string, tags: string[]) => void;
}) {
  const [stars, setStars] = useState(0);
  const [tags, setTags] = useState<string[]>([]);
  const [review, setReview] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const TAGS = ['On time', 'Professional', 'Friendly', 'Careful with items', 'Good communication', 'Clean truck', 'Would book again'];
  const handleSubmit = () => {
    if (!stars) return;
    onSubmit(job.id, stars, review, tags);
    setSubmitted(true);
    setTimeout(onClose, 1400);
  };
  if (submitted) {
    return <BottomSheet onClose={onClose} data-id="element-2228">
        <div className="p-8 text-center" data-id="element-2229">
          <motion.div initial={{
          scale: 0.5,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="w-16 h-16 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4" data-id="element-2230">
            <CheckCircle2 className="w-8 h-8" data-id="element-2231" />
          </motion.div>
          <h3 className="text-xl font-display font-bold text-gray-900 mb-1" data-id="element-2232">
            Thanks for rating!
          </h3>
          <p className="text-sm text-gray-500" data-id="element-2233">Your feedback helps others.</p>
        </div>
      </BottomSheet>;
  }
  return <BottomSheet onClose={onClose} data-id="element-2234">
      <div className="px-6 pb-6" data-id="element-2235">
        <div className="text-center mb-6" data-id="element-2236">
          <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-500 flex items-center justify-center mx-auto mb-3" data-id="element-2237">
            <Star className="w-6 h-6 fill-current" data-id="element-2238" />
          </div>
          <h3 className="text-xl font-display font-bold text-gray-900 mb-1" data-id="element-2239">
            How was your move?
          </h3>
          <p className="text-sm text-gray-500" data-id="element-2240">{job.supplierName}</p>
        </div>

        <div className="flex justify-center gap-2 mb-8" data-id="element-2241">
          {[1, 2, 3, 4, 5].map(s => <button key={s} onClick={() => setStars(s)} className="p-1 transition-transform active:scale-90" data-id="element-2242">
              <Star className={`w-10 h-10 ${s <= stars ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} data-id="element-2243" />
            </button>)}
        </div>

        <div className="mb-6" data-id="element-2244">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3" data-id="element-2245">
            What stood out?
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-2246">
            {TAGS.map(t => <button key={t} onClick={() => setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])} className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${tags.includes(t) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-100 bg-white text-gray-600'}`} data-id="element-2247">
                {t}
              </button>)}
          </div>
        </div>

        <div className="mb-6" data-id="element-2248">
          <textarea value={review} onChange={e => setReview(e.target.value)} rows={3} placeholder="Add a review (optional)..." className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none text-sm focus:border-brand-300 resize-none" data-id="element-2249" />
        </div>

        <button onClick={handleSubmit} disabled={!stars} className="w-full py-3.5 bg-gray-900 disabled:opacity-50 text-white rounded-xl font-bold text-sm" data-id="element-2250">
          Submit rating
        </button>
      </div>
    </BottomSheet>;
}
function ReceiptSheet({
  job,
  onClose
}: {
  job: MobileJobRecord;
  onClose: () => void;
}) {
  const total = job.price || 0;
  const vatRate = 0.05;
  const serviceRate = 0.05;
  const preVat = total / (1 + vatRate);
  const vat = Math.round(total - preVat);
  const subtotal = Math.round(preVat / (1 + serviceRate));
  const serviceFee = Math.round(preVat - subtotal);
  const invoiceNo = `INV-2026-${job.id.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(-6)}`;
  const issuedAt = job.completedAt || job.createdAt;
  return <BottomSheet onClose={onClose} data-id="element-2251">
      <div className="px-6 pb-6" data-id="element-2252">
        <div className="text-center mb-6 pb-6 border-b border-dashed border-gray-200" data-id="element-2253">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-100 mb-3" data-id="element-2254">
            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2255" />
            <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-2256">
              Paid
            </span>
          </div>
          <h2 className="text-2xl font-display font-black text-gray-900 mb-1" data-id="element-2257">
            Pair2 Move
          </h2>
          <p className="text-xs text-gray-500" data-id="element-2258">TRN AE100765432100</p>
        </div>

        <div className="space-y-4 mb-6" data-id="element-2259">
          <div className="flex justify-between text-sm" data-id="element-2260">
            <span className="text-gray-500" data-id="element-2261">Invoice no.</span>
            <span className="font-semibold text-gray-900" data-id="element-2262">{invoiceNo}</span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-2263">
            <span className="text-gray-500" data-id="element-2264">Issued</span>
            <span className="font-semibold text-gray-900" data-id="element-2265">
              {new Date(issuedAt).toLocaleDateString()}
            </span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-2266">
            <span className="text-gray-500" data-id="element-2267">Carrier</span>
            <span className="font-semibold text-gray-900" data-id="element-2268">
              {job.supplierName}
            </span>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-4 mb-6" data-id="element-2269">
          <p className="font-bold text-gray-900 text-sm mb-1" data-id="element-2270">
            {job.serviceName}
          </p>
          <p className="text-xs text-gray-500 mb-3" data-id="element-2271">{job.routeHeadline}</p>
          <div className="space-y-2 pt-3 border-t border-gray-200 text-sm" data-id="element-2272">
            <div className="flex justify-between" data-id="element-2273">
              <span className="text-gray-600" data-id="element-2274">Carrier fee</span>
              <span className="font-medium text-gray-900" data-id="element-2275">AED {subtotal}</span>
            </div>
            <div className="flex justify-between" data-id="element-2276">
              <span className="text-gray-600" data-id="element-2277">Service fee (5%)</span>
              <span className="font-medium text-gray-900" data-id="element-2278">
                AED {serviceFee}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2279">
              <span className="text-gray-600" data-id="element-2280">VAT (5%)</span>
              <span className="font-medium text-gray-900" data-id="element-2281">AED {vat}</span>
            </div>
          </div>
          <div className="flex justify-between items-end pt-3 mt-3 border-t border-gray-200" data-id="element-2282">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-2283">
              Total paid
            </span>
            <span className="text-2xl font-display font-bold text-gray-900" data-id="element-2284">
              AED {total}
            </span>
          </div>
        </div>

        <div className="flex gap-3" data-id="element-2285">
          <button onClick={onClose} className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm" data-id="element-2286">
            Close
          </button>
          <button onClick={() => window.print()} className="flex-1 py-3.5 bg-gray-900 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2" data-id="element-2287">
            <Download className="w-4 h-4" data-id="element-2288" /> Save PDF
          </button>
        </div>
      </div>
    </BottomSheet>;
}
function TrackingSheet({
  job,
  onClose,
  onCancel
}: {
  job: MobileJobRecord;
  onClose: () => void;
  onCancel: () => void;
}) {
  const steps = ['Confirmed', 'Driver assigned', 'On the way', 'Delivered'];
  const activeStep = 2; // Mock 'On the way'
  return <BottomSheet onClose={onClose} data-id="element-2289">
      <div className="px-6 pb-6" data-id="element-2290">
        <div className="flex items-center justify-between mb-6" data-id="element-2291">
          <div data-id="element-2292">
            <h3 className="text-xl font-display font-bold text-gray-900" data-id="element-2293">
              Arriving in ~14 min
            </h3>
            <p className="text-sm text-gray-500" data-id="element-2294">4.2 km away</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold text-lg" data-id="element-2295">
            {job.supplierName?.charAt(0) || 'D'}
          </div>
        </div>

        <div className="relative h-48 bg-gray-100 rounded-2xl mb-6 overflow-hidden border border-gray-200" data-id="element-2296">
          {/* Mock Map Background */}
          <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(#000 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-2297" />
          {/* Mock Route Path */}
          <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-2298">
            <path d="M 20 80 Q 50 80 50 50 T 80 20" fill="none" stroke="#cbd5e1" strokeWidth="4" strokeDasharray="6 6" data-id="element-2299" />
            <motion.path d="M 20 80 Q 50 80 50 50 T 80 20" fill="none" stroke="#3b82f6" strokeWidth="4" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 0.6
          }} transition={{
            duration: 2,
            ease: 'easeOut'
          }} data-id="element-2300" />
          </svg>
          <div className="absolute bottom-4 left-4 w-4 h-4 bg-brand-500 rounded-full border-2 border-white shadow-md animate-pulse" data-id="element-2301" />
          <div className="absolute top-4 right-4 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white shadow-md" data-id="element-2302" />
          <motion.div className="absolute w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-100" initial={{
          bottom: '16%',
          left: '16%'
        }} animate={{
          bottom: '46%',
          left: '46%'
        }} transition={{
          duration: 2,
          ease: 'easeOut'
        }} data-id="element-2303">
            <Truck className="w-4 h-4 text-brand-600" data-id="element-2304" />
          </motion.div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-4 mb-6" data-id="element-2305">
          <div className="flex items-center justify-between mb-4" data-id="element-2306">
            <div data-id="element-2307">
              <p className="font-bold text-gray-900 text-sm" data-id="element-2308">
                {job.supplierName}
              </p>
              <p className="text-xs text-gray-500" data-id="element-2309">
                {job.supplierTruck} • F-21934
              </p>
            </div>
            <div className="flex gap-2" data-id="element-2310">
              <button className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-700 shadow-sm" data-id="element-2311">
                <MessageCircle className="w-4 h-4" data-id="element-2312" />
              </button>
              <button className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-700 shadow-sm" data-id="element-2313">
                <Phone className="w-4 h-4" data-id="element-2314" />
              </button>
            </div>
          </div>
          <div className="space-y-4 relative before:absolute before:inset-0 before:ml-[11px] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gray-200" data-id="element-2315">
            {steps.map((step, i) => <div key={step} className="relative flex items-center gap-4" data-id="element-2316">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 border-2 ${i < activeStep ? 'bg-brand-500 border-brand-500 text-white' : i === activeStep ? 'bg-white border-brand-500 text-brand-500' : 'bg-white border-gray-300 text-transparent'}`} data-id="element-2317">
                  {i < activeStep && <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2318" />}
                  {i === activeStep && <div className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" data-id="element-2319" />}
                </div>
                <p className={`text-sm font-medium ${i <= activeStep ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-2320">
                  {step}
                </p>
              </div>)}
          </div>
        </div>

        <button onClick={onCancel} className="w-full py-3.5 text-rose-600 bg-rose-50 rounded-xl font-bold text-sm" data-id="element-2321">
          Cancel trip
        </button>
      </div>
    </BottomSheet>;
}
function BidsSheet({
  job,
  onClose,
  onAccept,
  onReject,
  onCancel
}: {
  job: MobileJobRecord;
  onClose: () => void;
  onAccept: (id: string, idx: number) => void;
  onReject: (id: string, idx: number) => void;
  onCancel: () => void;
}) {
  const bids = job.bids || [];
  const pending = bids.map((b, i) => ({
    ...b,
    originalIndex: i
  })).filter(b => !b.status || b.status === 'pending');
  const accepted = bids.map((b, i) => ({
    ...b,
    originalIndex: i
  })).filter(b => b.status === 'accepted');
  const rejected = bids.map((b, i) => ({
    ...b,
    originalIndex: i
  })).filter(b => b.status === 'rejected');
  const bestPending = pending.length ? pending.reduce((a, b) => a.price < b.price ? a : b) : null;
  return <BottomSheet onClose={onClose} data-id="element-2322">
      <div className="px-6 pb-6" data-id="element-2323">
        <div className="flex items-start gap-3 mb-6" data-id="element-2324">
          <div className="w-12 h-12 rounded-2xl bg-gray-900 text-white flex items-center justify-center shrink-0" data-id="element-2325">
            <Gavel className="w-6 h-6" data-id="element-2326" />
          </div>
          <div data-id="element-2327">
            <h3 className="text-lg font-display font-bold text-gray-900 leading-tight" data-id="element-2328">
              Bidding • {job.serviceName}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-2329">{job.routeHeadline}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-6" data-id="element-2330">
          <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100" data-id="element-2331">
            <p className="text-xl font-display font-bold text-amber-700" data-id="element-2332">
              {pending.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600" data-id="element-2333">
              Pending
            </p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100" data-id="element-2334">
            <p className="text-xl font-display font-bold text-green-700" data-id="element-2335">
              {accepted.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-green-600" data-id="element-2336">
              Accepted
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200" data-id="element-2337">
            <p className="text-xl font-display font-bold text-gray-700" data-id="element-2338">
              {rejected.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-2339">
              Rejected
            </p>
          </div>
        </div>

        <div className="space-y-6 mb-6" data-id="element-2340">
          {pending.length > 0 && <div data-id="element-2341">
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-2342">
                <Sparkles className="w-4 h-4 text-brand-500" data-id="element-2343" /> Live bids
              </h4>
              <div className="space-y-3" data-id="element-2344">
                {pending.map(b => <div key={b.originalIndex} className={`p-3 rounded-2xl border ${b.originalIndex === bestPending?.originalIndex ? 'bg-brand-50 border-brand-200' : 'bg-white border-gray-200'}`} data-id="element-2345">
                    <div className="flex justify-between items-start mb-3" data-id="element-2346">
                      <div className="flex items-center gap-3" data-id="element-2347">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs ${b.color}`} data-id="element-2348">
                          {b.initials}
                        </div>
                        <div data-id="element-2349">
                          <p className="text-sm font-bold text-gray-900" data-id="element-2350">
                            {b.supplier}
                          </p>
                          <p className="text-[10px] text-gray-500" data-id="element-2351">
                            {b.rating} ★ •{' '}
                            {new Date(b.receivedAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-2352">
                        <p className="font-display font-bold text-gray-900" data-id="element-2353">
                          AED {b.price}
                        </p>
                        {b.originalIndex === bestPending?.originalIndex && <span className="text-[9px] font-bold uppercase tracking-wider bg-brand-500 text-white px-1.5 py-0.5 rounded" data-id="element-2354">
                            Best
                          </span>}
                      </div>
                    </div>
                    <div className="flex gap-2" data-id="element-2355">
                      <button onClick={() => onReject(job.id, b.originalIndex)} className="flex-1 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-bold" data-id="element-2356">
                        Reject
                      </button>
                      <button onClick={() => {
                  onAccept(job.id, b.originalIndex);
                  onClose();
                }} className="flex-1 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold" data-id="element-2357">
                        Accept
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {accepted.length > 0 && <div data-id="element-2358">
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-2359">
                <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-2360" /> Accepted
              </h4>
              {accepted.map(b => <div key={b.originalIndex} className="p-3 rounded-2xl bg-green-50 border border-green-100 flex justify-between items-center opacity-80" data-id="element-2361">
                  <div className="flex items-center gap-3" data-id="element-2362">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-[10px] ${b.color}`} data-id="element-2363">
                      {b.initials}
                    </div>
                    <p className="text-sm font-bold text-gray-900" data-id="element-2364">
                      {b.supplier}
                    </p>
                  </div>
                  <p className="font-display font-bold text-gray-900" data-id="element-2365">
                    AED {b.price}
                  </p>
                </div>)}
            </div>}
        </div>

        <button onClick={onCancel} className="w-full py-3.5 text-rose-600 bg-rose-50 rounded-xl font-bold text-sm" data-id="element-2366">
          Cancel request
        </button>
      </div>
    </BottomSheet>;
}
function CancelSheet({
  job,
  onClose,
  onConfirm
}: {
  job: MobileJobRecord;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return <BottomSheet onClose={onClose} data-id="element-2367">
      <div className="px-6 pb-6 text-center" data-id="element-2368">
        <div className="w-16 h-16 rounded-3xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-4" data-id="element-2369">
          <AlertCircle className="w-8 h-8" data-id="element-2370" />
        </div>
        <h3 className="text-xl font-display font-bold text-gray-900 mb-2" data-id="element-2371">
          Cancel this {job.type === 'bidding' ? 'bidding' : 'job'}?
        </h3>
        <p className="text-sm text-gray-500 mb-6" data-id="element-2372">
          {job.type === 'bidding' ? "We'll notify the carrier network that the request is closed." : 'A cancellation fee may apply if the driver is already on the way.'}
        </p>
        <div className="bg-gray-50 rounded-2xl p-4 mb-6 text-left" data-id="element-2373">
          <p className="font-bold text-gray-900 text-sm mb-1" data-id="element-2374">
            {job.serviceName}
          </p>
          <p className="text-xs text-gray-500" data-id="element-2375">{job.routeHeadline}</p>
        </div>
        <div className="flex gap-3" data-id="element-2376">
          <button onClick={onClose} className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm" data-id="element-2377">
            Keep it
          </button>
          <button onClick={onConfirm} className="flex-1 py-3.5 bg-rose-500 text-white rounded-xl font-bold text-sm" data-id="element-2378">
            Yes, cancel
          </button>
        </div>
      </div>
    </BottomSheet>;
}
function PaySheet({
  job,
  onClose,
  onPaid
}: {
  job: MobileJobRecord;
  onClose: () => void;
  onPaid: () => void;
}) {
  type Stage = 'idle' | 'processing' | 'success';
  const [stage, setStage] = useState<Stage>('idle');
  const [method, setMethod] = useState<string>('card-1');
  const total = job.price || 0;
  const Icon = SERVICE_ICON_MAP[job.serviceId] || Package;
  const iconColor = SERVICE_COLOR_MAP[job.serviceId] || 'bg-gray-100 text-gray-600';
  const METHODS = [{
    id: 'apple',
    label: 'Apple Pay',
    sub: 'Face ID',
    brand: 'Pay'
  }, {
    id: 'card-1',
    label: 'Visa •••• 4242',
    sub: 'Default · expires 09/27',
    brand: 'VISA'
  }, {
    id: 'card-2',
    label: 'Mastercard •••• 8821',
    sub: 'Personal',
    brand: 'MC'
  }];
  const handlePay = () => {
    if (stage !== 'idle') return;
    setStage('processing');
    setTimeout(() => {
      setStage('success');
      setTimeout(() => {
        onPaid();
      }, 1100);
    }, 1400);
  };
  return <BottomSheet onClose={stage === 'idle' ? onClose : () => {}} data-id="element-2379">
      <div className="px-6 pb-6" data-id="element-2380">
        {stage === 'success' ? <div className="py-10 text-center" data-id="element-2381">
            <motion.div initial={{
          scale: 0.4,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 18
        }} className="w-20 h-20 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-5" data-id="element-2382">
              <CheckCircle2 className="w-10 h-10" data-id="element-2383" />
            </motion.div>
            <h3 className="text-xl font-display font-bold text-gray-900 mb-1" data-id="element-2384">
              Payment successful
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-2385">
              AED {total} charged · your job is now active
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 text-xs text-gray-500" data-id="element-2386">
              <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-2387" />
              Moving to active jobs…
            </div>
          </div> : <>
            <div className="flex items-center justify-between mb-5" data-id="element-2388">
              <div data-id="element-2389">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-0.5" data-id="element-2390">
                  Total due
                </p>
                <p className="text-3xl font-display font-black text-gray-900 tabular-nums" data-id="element-2391">
                  AED {total}
                </p>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" data-id="element-2392">
                <Shield className="w-3 h-3" data-id="element-2393" />
                Secure
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-3.5 mb-5 flex items-center gap-3" data-id="element-2394">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconColor}`} data-id="element-2395">
                <Icon className="w-5 h-5" data-id="element-2396" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-2397">
                <p className="font-bold text-gray-900 text-sm leading-tight" data-id="element-2398">
                  {job.serviceName}
                </p>
                <p className="text-xs text-gray-500 truncate" data-id="element-2399">
                  {job.routeHeadline}
                </p>
              </div>
              {job.supplierName && <div className="text-right shrink-0" data-id="element-2400">
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider" data-id="element-2401">
                    Carrier
                  </p>
                  <p className="text-xs font-bold text-gray-700 truncate max-w-[100px]" data-id="element-2402">
                    {job.supplierName}
                  </p>
                </div>}
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2" data-id="element-2403">
              Payment method
            </p>
            <div className="space-y-2 mb-6" data-id="element-2404">
              {METHODS.map(m => {
            const isActive = method === m.id;
            return <button key={m.id} onClick={() => setMethod(m.id)} className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${isActive ? 'border-gray-900 bg-white shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-2405">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-700 font-black text-[10px] shrink-0" data-id="element-2406">
                      {m.brand}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2407">
                      <p className="text-sm font-bold text-gray-900 leading-tight" data-id="element-2408">
                        {m.label}
                      </p>
                      <p className="text-[11px] text-gray-500" data-id="element-2409">{m.sub}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isActive ? 'border-gray-900 bg-gray-900' : 'border-gray-300'}`} data-id="element-2410">
                      {isActive && <div className="w-2 h-2 rounded-full bg-white" data-id="element-2411" />}
                    </div>
                  </button>;
          })}
            </div>
            <button onClick={handlePay} disabled={stage === 'processing'} className="w-full py-3.5 bg-gray-900 text-white rounded-2xl font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-80" data-id="element-2412">
              {stage === 'processing' ? <>
                  <Loader2 className="w-4 h-4 animate-spin" data-id="element-2413" />
                  Processing payment…
                </> : <>
                  <Shield className="w-4 h-4" data-id="element-2414" />
                  Pay AED {total}
                </>}
            </button>
            <p className="text-[10px] text-gray-400 text-center mt-3 leading-snug" data-id="element-2415">
              You will be charged AED {total}. Funds are held until delivery is
              confirmed.
            </p>
          </>}
      </div>
    </BottomSheet>;
}
const SERVICE_ICON_MAP: Record<string, typeof HomeIcon> = {
  recovery: Truck,
  home: HomeIcon,
  furniture: Sofa,
  delivery: Package,
  reefer: Snowflake,
  equipment: Wrench
};
const SERVICE_COLOR_MAP: Record<string, string> = {
  recovery: 'bg-red-100 text-red-600',
  home: 'bg-emerald-100 text-emerald-600',
  furniture: 'bg-sky-100 text-sky-600',
  delivery: 'bg-amber-100 text-amber-600',
  reefer: 'bg-cyan-100 text-cyan-600',
  equipment: 'bg-fuchsia-100 text-fuchsia-600'
};
function formatThreadTime(ts: number): string {
  const now = new Date();
  const d = new Date(ts);
  const diffMs = now.getTime() - ts;
  const isToday = now.toDateString() === d.toDateString();
  if (isToday) {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (yesterday.toDateString() === d.toDateString()) return 'Yesterday';
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays < 7) return d.toLocaleDateString(undefined, {
    weekday: 'short'
  });
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short'
  });
}
function ChatThreadsList({
  chats,
  onResume,
  onCompose,
  onDelete
}: {
  chats: ChatSession[];
  onResume: (chat: ChatSession) => void;
  onCompose: () => void;
  onDelete: (chatId: string) => void;
}) {
  const sortedChats = [...chats].sort((a, b) => b.updatedAt - a.updatedAt);
  const unreadCount = chats.filter(c => c.unread).length;
  const [confirmDelete, setConfirmDelete] = useState<ChatSession | null>(null);
  return <div className="flex flex-col h-full bg-white relative" data-id="element-2416">
      {/* Header */}
      <div className="px-5 py-3 border-b border-gray-100 bg-white shrink-0 z-10 sticky top-0" data-id="element-2417">
        <div className="flex items-center justify-between" data-id="element-2418">
          <div data-id="element-2419">
            <h1 className="text-xl font-display font-bold text-gray-900 tracking-tight" data-id="element-2420">
              Requests
            </h1>
            <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-2421">
              {chats.length} request{chats.length === 1 ? '' : 's'}
              {unreadCount > 0 && ` · ${unreadCount} unread`}
            </p>
          </div>
          <button onClick={onCompose} className="w-10 h-10 rounded-full orange-gradient-bg text-white flex items-center justify-center shadow-md shadow-brand-500/30 active:scale-95 transition-transform" title="New request" aria-label="New request" data-id="element-2422">
            <Plus className="w-5 h-5" data-id="element-2423" />
          </button>
        </div>
      </div>

      {/* Threads */}
      <div className="flex-1 overflow-y-auto scrollbar-hide pb-24" data-id="element-2424">
        {sortedChats.length === 0 ? <div className="px-6 py-16 text-center" data-id="element-2425">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 text-gray-400 mx-auto mb-3 flex items-center justify-center" data-id="element-2426">
              <MessageCircle className="w-7 h-7" data-id="element-2427" />
            </div>
            <p className="text-sm font-bold text-gray-900" data-id="element-2428">No requests yet</p>
            <p className="text-xs text-gray-500 mt-1 mb-5 max-w-[220px] mx-auto" data-id="element-2429">
              Start a new request to find the right supplier at the best price.
            </p>
            <button onClick={onCompose} className="inline-flex items-center gap-1.5 px-4 py-2 orange-gradient-bg text-white rounded-full text-xs font-bold active:scale-95 transition-transform" data-id="element-2430">
              <Plus className="w-3.5 h-3.5" data-id="element-2431" />
              New request
            </button>
          </div> : <div data-id="element-2432">
            <AnimatePresence initial={false} data-id="element-2433">
              {sortedChats.map((c, idx) => {
            const Icon = SERVICE_ICON_MAP[c.serviceId] || MessageCircle;
            const colorClass = SERVICE_COLOR_MAP[c.serviceId] || 'bg-gray-100 text-gray-600';
            const expired = Date.now() - c.createdAt > TWENTY_FOUR_HOURS;
            return <SwipeableChatRow key={c.id} index={idx} onDelete={() => setConfirmDelete(c)} onTap={() => onResume(c)} isLast={idx === sortedChats.length - 1} data-id="element-2434">
                    <div className="w-full px-5 py-3 flex items-start gap-3 text-left bg-white" data-id="element-2435">
                      <div className="relative shrink-0" data-id="element-2436">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colorClass}`} data-id="element-2437">
                          <Icon className="w-5 h-5" data-id="element-2438" />
                        </div>
                        {c.unread && <span className="absolute top-0 right-0 w-3 h-3 rounded-full bg-brand-500 ring-2 ring-white" data-id="element-2439" />}
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2440">
                        <div className="flex items-start justify-between gap-2 mb-0.5" data-id="element-2441">
                          <p className={`text-[14px] truncate ${c.unread ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`} data-id="element-2442">
                            {c.summary}
                          </p>
                          <span className={`text-[10px] shrink-0 whitespace-nowrap mt-0.5 ${c.unread ? 'text-brand-600 font-bold' : 'text-gray-400 font-medium'}`} data-id="element-2443">
                            {formatThreadTime(c.updatedAt)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-2444">
                          <p className={`text-[12px] truncate flex-1 ${c.unread ? 'text-gray-700 font-medium' : 'text-gray-500'}`} data-id="element-2445">
                            {c.lastMessage}
                          </p>
                          {c.status === 'booked' && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 shrink-0 inline-flex items-center gap-0.5" data-id="element-2446">
                              <CheckCircle2 className="w-2.5 h-2.5" data-id="element-2447" />
                              Booked
                            </span>}
                          {c.status === 'in_transit' && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 shrink-0 inline-flex items-center gap-0.5" data-id="element-2448">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" data-id="element-2449" />
                              On the way
                            </span>}
                          {c.status === 'awaiting_rating' && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0 inline-flex items-center gap-0.5" data-id="element-2450">
                              <Star className="w-2.5 h-2.5 fill-current" data-id="element-2451" />
                              Rate
                            </span>}
                          {c.status === 'completed' && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 shrink-0 inline-flex items-center gap-0.5" data-id="element-2452">
                              <CheckCircle2 className="w-2.5 h-2.5" data-id="element-2453" />
                              Completed
                            </span>}
                          {c.status === 'expired' || expired && c.status === 'active' ? <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0 inline-flex items-center gap-0.5" data-id="element-2454">
                              <AlertTriangle className="w-2.5 h-2.5" data-id="element-2455" />
                              Expired
                            </span> : null}
                        </div>
                      </div>
                    </div>
                  </SwipeableChatRow>;
          })}
            </AnimatePresence>
          </div>}
      </div>

      {/* Floating compose button (always available) */}
      {sortedChats.length > 0 && <button onClick={onCompose} className="absolute bottom-5 right-5 w-14 h-14 rounded-full orange-gradient-bg text-white flex items-center justify-center shadow-xl shadow-brand-500/40 active:scale-95 transition-transform z-20" aria-label="New request" data-id="element-2456">
          <Plus className="w-6 h-6" data-id="element-2457" />
        </button>}

      {/* Delete confirmation */}
      <AnimatePresence data-id="element-2458">
        {confirmDelete && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setConfirmDelete(null)} className="absolute inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end" data-id="element-2459">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} onClick={e => e.stopPropagation()} className="w-full bg-white rounded-t-3xl p-6 relative" data-id="element-2460">
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-gray-200 rounded-full" data-id="element-2461" />
              <div className="text-center pt-3" data-id="element-2462">
                <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-3" data-id="element-2463">
                  <Trash2 className="w-7 h-7" data-id="element-2464" />
                </div>
                <h3 className="text-lg font-display font-bold text-gray-900 mb-1" data-id="element-2465">
                  Delete this chat?
                </h3>
                <p className="text-sm text-gray-500 mb-5" data-id="element-2466">
                  "{confirmDelete.summary}" will be permanently removed.
                </p>
                <div className="flex gap-3" data-id="element-2467">
                  <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm" data-id="element-2468">
                    Keep
                  </button>
                  <button onClick={() => {
                onDelete(confirmDelete.id);
                setConfirmDelete(null);
              }} className="flex-1 py-3 bg-rose-500 text-white rounded-xl font-bold text-sm" data-id="element-2469">
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function SwipeableChatRow({
  index,
  isLast,
  onDelete,
  onTap,
  children
}: {
  index: number;
  isLast: boolean;
  onDelete: () => void;
  onTap: () => void;
  children: React.ReactNode;
}) {
  const [dragX, setDragX] = useState(0);
  const dragStartX = useRef(0);
  const REVEAL = 88;
  return <motion.div layout initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.2
    }
  }} transition={{
    delay: index * 0.04
  }} className={`relative overflow-hidden ${!isLast ? 'border-b border-gray-100' : ''}`} data-id="element-2470">
      {/* Delete reveal background */}
      <button onClick={onDelete} className="absolute inset-y-0 right-0 w-[88px] bg-rose-500 text-white flex flex-col items-center justify-center gap-1 active:bg-rose-600" aria-label="Delete chat" data-id="element-2471">
        <Trash2 className="w-5 h-5" data-id="element-2472" />
        <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-2473">
          Delete
        </span>
      </button>

      {/* Foreground row, draggable */}
      <motion.div drag="x" dragConstraints={{
      left: -REVEAL,
      right: 0
    }} dragElastic={0.1} dragMomentum={false} animate={{
      x: dragX
    }} onDragStart={(_, info) => {
      dragStartX.current = info.point.x;
    }} onDragEnd={(_, info) => {
      const offset = info.offset.x;
      if (offset < -REVEAL / 2) {
        setDragX(-REVEAL);
      } else {
        setDragX(0);
      }
    }} onClick={() => {
      if (dragX < 0) {
        setDragX(0);
      } else {
        onTap();
      }
    }} className="relative bg-white cursor-pointer active:bg-gray-50" data-id="element-2474">
        {children}
      </motion.div>
    </motion.div>;
}
function NewChatPrompt({
  onSubmit,
  onBack
}: {
  onSubmit: (text: string) => void;
  onBack: () => void;
}) {
  const [input, setInput] = useState('');
  const submit = (text: string) => {
    const v = text.trim();
    if (!v) return;
    onSubmit(v);
  };
  return <div className="flex flex-col h-full bg-gray-50 relative" data-id="element-2475">
      <div className="px-3 py-3 border-b border-gray-100 bg-white/95 backdrop-blur-md shrink-0 z-10 sticky top-0" data-id="element-2476">
        <div className="flex items-center gap-2" data-id="element-2477">
          <button onClick={onBack} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-700 transition-colors shrink-0" aria-label="Back" data-id="element-2478">
            <ChevronLeft className="w-5 h-5" data-id="element-2479" />
          </button>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shrink-0" data-id="element-2480">
            <Sparkles className="w-4 h-4 text-white" data-id="element-2481" />
          </div>
          <div className="min-w-0" data-id="element-2482">
            <p className="font-bold text-gray-900 text-[13px] leading-tight truncate" data-id="element-2483">
              New request
            </p>
            <p className="text-[10px] text-brand-600 font-medium truncate" data-id="element-2484">
              Pair2 AI · ready to help
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-5 scrollbar-hide pb-32" data-id="element-2485">
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex gap-2 max-w-[92%]" data-id="element-2486">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shrink-0 mt-1" data-id="element-2487">
            <Sparkles className="w-3.5 h-3.5 text-white" data-id="element-2488" />
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3.5 py-3 shadow-sm" data-id="element-2489">
            <p className="text-[13px] text-gray-800 leading-relaxed" data-id="element-2490">
              Hi 👋 I'm your Pair2 AI assistant.
            </p>
            <p className="text-[13px] text-gray-800 leading-relaxed mt-1.5" data-id="element-2491">
              Tell me what service you need and I'll find the right supplier at
              the best price.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="pl-9" data-id="element-2492">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" data-id="element-2493">
            Quick start
          </p>
          <div className="flex flex-wrap gap-1.5" data-id="element-2494">
            {DISCOVERY_CHIPS.map(c => {
            const Icon = c.icon;
            return <button key={c.label} onClick={() => submit(c.label)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-brand-200 text-brand-700 rounded-full text-[11px] font-medium shadow-sm active:scale-95 transition-transform" data-id="element-2495">
                  <Icon className="w-3 h-3" data-id="element-2496" />
                  {c.label}
                </button>;
          })}
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 p-3 pb-safe" data-id="element-2497">
        <div className="relative flex items-center gap-2" data-id="element-2498">
          <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') submit(input);
        }} placeholder="What service do you need?" className="flex-1 bg-gray-100 border-transparent rounded-full py-2.5 px-4 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" autoFocus data-id="element-2499" />
          <button onClick={() => submit(input)} disabled={!input.trim()} className="w-9 h-9 rounded-full bg-brand-500 text-white flex items-center justify-center disabled:opacity-50 transition-opacity shrink-0" data-id="element-2500">
            <Send className="w-4 h-4 -ml-0.5" data-id="element-2501" />
          </button>
        </div>
      </div>
    </div>;
}
function AgenticChatTab({
  chatId,
  serviceId,
  initialPrompt,
  createdAt,
  onBack,
  onNewChat,
  onBook,
  onViewJobs
}: {
  chatId: string;
  serviceId: ServiceId;
  initialPrompt: string;
  createdAt: number;
  onBack: () => void;
  onNewChat: () => void;
  onBook: (job: MobileJobRecord) => void;
  onViewJobs: () => void;
}) {
  const service = getServiceConfig(serviceId);
  const slots = getSlotsForService(serviceId);
  const turnIdRef = useRef(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bidIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chatExpired = Date.now() - createdAt > TWENTY_FOUR_HOURS;
  const [thinking, setThinking] = useState(true);
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [values, setValues] = useState<SlotValues>({});
  const [activeSlot, setActiveSlot] = useState<Slot | null>(null);
  const [input, setInput] = useState('');
  const [isDone, setIsDone] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  // Offer stage state
  const [offerStage, setOfferStage] = useState<OfferStage>('offers');
  const [counterSheetFor, setCounterSheetFor] = useState<CarrierOffer | null>(null);
  const [biddingSheetOpen, setBiddingSheetOpen] = useState(false);
  const [counterPrice, setCounterPrice] = useState('');
  const [counterNote, setCounterNote] = useState('');
  const [counterRound, setCounterRound] = useState(0);
  const [negotiationCarrier, setNegotiationCarrier] = useState<CarrierOffer | null>(null);
  const [carrierCounterPrice, setCarrierCounterPrice] = useState<number | null>(null);
  const [bidBudget, setBidBudget] = useState('');
  const [bidWindow, setBidWindow] = useState('15');
  const [liveBids, setLiveBids] = useState<BidEntry[]>([]);
  const [confirmedBooking, setConfirmedBooking] = useState<{
    carrier: string;
    price: number;
    tripId: string;
    eta: string;
  } | null>(null);
  // Boot conversation — progressive: user msg → thinking → AI greeting
  useEffect(() => {
    const promptToUse = initialPrompt || service.defaultPrompt;
    const prefilled = parseInitialPrompt(promptToUse, service.id);
    const cleaned: SlotValues = {
      ...prefilled
    };
    setValues(cleaned);
    // Step 1: show user message immediately
    if (initialPrompt) {
      const userTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'user',
        text: promptToUse
      };
      setTurns([userTurn]);
    } else {
      setTurns([]);
    }
    setThinking(true);
    // Step 2: after typing delay, AI responds
    const t1 = setTimeout(() => {
      const greetingText = getInitialGreeting(service.id, promptToUse, cleaned, slots);
      const next = nextMissingSlot(cleaned, slots);
      let aiText = greetingText;
      let suggestions: string[] | undefined;
      let showUploader = false;
      let isComplete = false;
      if (next) {
        aiText += `\n\n${next.askPhrases[0]}`;
        suggestions = next.suggestions;
        showUploader = next.kind === 'photos';
      } else {
        aiText = getCompletionMessage(service.id);
        isComplete = true;
      }
      const aiTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'ai',
        text: aiText,
        suggestions,
        showUploader,
        isComplete
      };
      setTurns(prev => [...prev, aiTurn]);
      setActiveSlot(next);
      setThinking(false);
      if (!next) setIsDone(true);
    }, 1100);
    return () => clearTimeout(t1);
  }, [service.id, initialPrompt]);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, thinking, offerStage, liveBids]);
  const submitReply = (rawText: string) => {
    if (!rawText.trim() || !activeSlot) return;
    if (activeSlot.kind === 'photos') {
      const skipPattern = /^(no|nope|skip|none|no photo|no photos|no pictures|no pics|no now|later|pass)\b/i;
      if (skipPattern.test(rawText.trim())) {
        const nextValues = {
          ...values,
          [activeSlot.id]: ['done']
        };
        setValues(nextValues);
        const upcoming = nextMissingSlot(nextValues, slots);
        advanceConversation(rawText, nextValues, upcoming, '✓ Skipped photos.');
        return;
      }
    }
    const parsed = parseUserReply(rawText, activeSlot);
    const nextValues = {
      ...values,
      ...parsed
    };
    if ('__bonus_location__' in parsed) {
      const bonusValue = parsed['__bonus_location__'];
      delete nextValues['__bonus_location__'];
      const nextLocSlot = slots.find(s => s.kind === 'location' && s.id !== activeSlot.id && (nextValues[s.id] === undefined || nextValues[s.id] === ''));
      if (nextLocSlot) {
        nextValues[nextLocSlot.id] = bonusValue;
      }
    }
    setValues(nextValues);
    const ack = parsed[activeSlot.id] !== undefined ? formatAcknowledgement(activeSlot, parsed[activeSlot.id]) : '';
    const nextSlot = nextMissingSlot(nextValues, slots);
    advanceConversation(rawText, nextValues, nextSlot, ack);
    setInput('');
  };
  const advanceConversation = (userText: string, nextValues: SlotValues, nextSlot: Slot | null, ack: string) => {
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: userText
    };
    let aiText = ack;
    let suggestions: string[] | undefined;
    let showUploader = false;
    let isComplete = false;
    if (nextSlot) {
      aiText += (aiText ? '\n\n' : '') + nextSlot.askPhrases[0];
      suggestions = nextSlot.suggestions;
      showUploader = nextSlot.kind === 'photos';
    } else {
      aiText += (aiText ? '\n\n' : '') + getCompletionMessage(service.id);
      isComplete = true;
    }
    const aiTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'ai',
      text: aiText,
      suggestions,
      showUploader,
      isComplete
    };
    setTurns(prev => [...prev, userTurn]);
    setThinking(true);
    setTimeout(() => {
      setTurns(prev => [...prev, aiTurn]);
      setActiveSlot(nextSlot);
      setThinking(false);
      if (!nextSlot) setIsDone(true);
    }, 700);
  };
  const handlePhotoUpload = () => {
    if (!activeSlot || activeSlot.kind !== 'photos') return;
    const newCount = photoCount + 1;
    setPhotoCount(newCount);
    const SAMPLE_PHOTOS = ['https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop', 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=400&h=300&fit=crop', 'https://images.unsplash.com/photo-1567016432779-094069958ea5?w=400&h=300&fit=crop', 'https://images.unsplash.com/photo-1558997519-83ea9252edf8?w=400&h=300&fit=crop', 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&h=300&fit=crop'];
    const photoUrl = SAMPLE_PHOTOS[(newCount - 1) % SAMPLE_PHOTOS.length];
    const attachments = [{
      url: photoUrl,
      type: 'image' as const
    }];
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: `📷 Photo ${newCount} uploaded`,
      attachments
    };
    setTurns(prev => [...prev, userTurn]);
    if (newCount >= 2) {
      const nextValues = {
        ...values,
        [activeSlot.id]: ['photo1', 'photo2']
      };
      setValues(nextValues);
      const upcoming = nextMissingSlot(nextValues, slots);
      setThinking(true);
      setTimeout(() => {
        let aiText = `✓ Got ${newCount} photos.`;
        let suggestions: string[] | undefined;
        let showUploader = false;
        let isComplete = false;
        if (upcoming) {
          aiText += `\n\n${upcoming.askPhrases[0]}`;
          suggestions = upcoming.suggestions;
          showUploader = upcoming.kind === 'photos';
        } else {
          aiText += `\n\n${getCompletionMessage(service.id)}`;
          isComplete = true;
        }
        const aiTurn: ConversationTurn = {
          id: turnIdRef.current++,
          role: 'ai',
          text: aiText,
          suggestions,
          showUploader,
          isComplete
        };
        setTurns(prev => [...prev, aiTurn]);
        setActiveSlot(upcoming);
        setThinking(false);
        if (!upcoming) setIsDone(true);
      }, 700);
    } else {
      // First photo received — acknowledge and prompt for one more so the
      // chat doesn't appear stalled while we wait for the second photo.
      setThinking(true);
      setTimeout(() => {
        const aiTurn: ConversationTurn = {
          id: turnIdRef.current++,
          role: 'ai',
          text: `✓ Got photo ${newCount}. One more please — a wider angle helps carriers quote accurately.`,
          showUploader: true
        };
        setTurns(prev => [...prev, aiTurn]);
        setThinking(false);
      }, 600);
    }
  };
  const confirmBooking = (carrier: string, price: number, eta: string) => {
    const tripId = `TRP-${Math.floor(10000 + Math.random() * 90000)}`;
    const newJob: MobileJobRecord = {
      id: tripId,
      type: 'booking',
      status: 'active',
      serviceId: service.id,
      serviceName: service.name,
      prompt: initialPrompt,
      routeHeadline: `${typeof values.pickup === 'string' ? values.pickup : 'Pickup'} → ${typeof values.dropoff === 'string' ? values.dropoff : 'Dropoff'}`,
      pickup: typeof values.pickup === 'string' ? values.pickup : 'Pickup location',
      dropoff: typeof values.dropoff === 'string' ? values.dropoff : 'Dropoff location',
      supplierName: carrier,
      price,
      createdAt: Date.now(),
      startedAt: Date.now()
    };
    onBook(newJob);
    setConfirmedBooking({
      carrier,
      price,
      tripId,
      eta
    });
    setOfferStage('confirmed');
    const aiTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'ai',
      text: `🎉 Booked with ${carrier} at AED ${price}. Trip ${tripId} created.`
    };
    setTurns(prev => [...prev, aiTurn]);
  };
  const openCounterSheet = (offer: CarrierOffer) => {
    if (chatExpired) return;
    setCounterSheetFor(offer);
    setCounterPrice(String(Math.round(offer.price * 0.85)));
    setCounterNote('');
  };
  const handleSendCounter = () => {
    if (!counterSheetFor || !counterPrice) return;
    const targetPrice = parseInt(counterPrice);
    const carrier = counterSheetFor;
    setNegotiationCarrier(carrier);
    const round = counterRound + 1;
    setCounterRound(round);
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: `Counter offer to ${carrier.name}: AED ${targetPrice}${counterNote ? ' — ' + counterNote : ''}`
    };
    setTurns(prev => [...prev, userTurn]);
    setCounterSheetFor(null);
    setOfferStage('counter-waiting');
    setThinking(true);
    setTimeout(() => {
      const sentTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'ai',
        text: `📡 Counter sent to ${carrier.name} (AED ${targetPrice}). Waiting for their response…`
      };
      setTurns(prev => [...prev, sentTurn]);
    }, 400);
    setTimeout(() => {
      setThinking(false);
      const outcome = round >= 3 ? Math.random() < 0.7 ? 'accept' : 'decline' : Math.random() < 0.4 ? 'accept' : Math.random() < 0.75 ? 'counter' : 'decline';
      if (outcome === 'accept') {
        const acceptTurn: ConversationTurn = {
          id: turnIdRef.current++,
          role: 'ai',
          text: `✅ ${carrier.name} accepted your counter of AED ${targetPrice}. Tap to confirm the booking.`
        };
        setTurns(prev => [...prev, acceptTurn]);
        setCarrierCounterPrice(targetPrice);
        setOfferStage('counter-response');
      } else if (outcome === 'counter') {
        const midpoint = Math.round((targetPrice + carrier.price) / 2);
        const counterTurn: ConversationTurn = {
          id: turnIdRef.current++,
          role: 'ai',
          text: `↩️ ${carrier.name} sent another counter offer: AED ${midpoint}. ${round >= 2 ? 'Last round — accept or walk away.' : 'Accept, counter again, or back out.'}`
        };
        setTurns(prev => [...prev, counterTurn]);
        setCarrierCounterPrice(midpoint);
        setOfferStage('counter-response');
      } else {
        const declineTurn: ConversationTurn = {
          id: turnIdRef.current++,
          role: 'ai',
          text: `❌ ${carrier.name} couldn't accept that price. Pick another carrier or open the job for bidding.`
        };
        setTurns(prev => [...prev, declineTurn]);
        setOfferStage('offers');
        setNegotiationCarrier(null);
        setCounterRound(0);
        setCarrierCounterPrice(null);
      }
    }, 2600);
  };
  const acceptCarrierCounter = () => {
    if (!negotiationCarrier || carrierCounterPrice == null) return;
    confirmBooking(negotiationCarrier.name, carrierCounterPrice, negotiationCarrier.eta);
  };
  const counterAgain = () => {
    if (!negotiationCarrier || carrierCounterPrice == null) return;
    setCounterSheetFor(negotiationCarrier);
    setCounterPrice(String(Math.max(carrierCounterPrice - 20, 50)));
    setCounterNote('');
  };
  const cancelNegotiation = () => {
    setNegotiationCarrier(null);
    setCounterRound(0);
    setCarrierCounterPrice(null);
    setOfferStage('offers');
    const cancelTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'ai',
      text: "Back to all offers. Pick a carrier when you're ready."
    };
    setTurns(prev => [...prev, cancelTurn]);
  };
  const handleStartBidding = () => {
    if (!bidBudget) return;
    setBiddingSheetOpen(false);
    setOfferStage('bidding-live');
    setLiveBids([]);
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: `Open for bidding · Max AED ${bidBudget} · ${bidWindow} min window`
    };
    setTurns(prev => [...prev, userTurn]);
    setTimeout(() => {
      const liveTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'ai',
        text: '🔴 Your job is live on the load board. Carriers are bidding now…'
      };
      setTurns(prev => [...prev, liveTurn]);
    }, 350);
    let count = 0;
    const max = parseInt(bidBudget);
    const usedCarriers = new Set<string>();
    bidIntervalRef.current = setInterval(() => {
      count++;
      const available = BID_CARRIERS.filter(c => !usedCarriers.has(c.name));
      if (available.length === 0) return;
      const pick = available[Math.floor(Math.random() * available.length)];
      usedCarriers.add(pick.name);
      const newBid: BidEntry = {
        id: `bid-${count}`,
        carrier: pick.name,
        rating: pick.rating,
        price: Math.max(max - Math.floor(40 + Math.random() * 80) - count * 8, Math.round(max * 0.6))
      };
      setLiveBids(prev => [newBid, ...prev]);
      if (count >= 4) {
        if (bidIntervalRef.current) clearInterval(bidIntervalRef.current);
        setTimeout(() => {
          setOfferStage('bidding-complete');
          const completeTurn: ConversationTurn = {
            id: turnIdRef.current++,
            role: 'ai',
            text: '✅ Bidding closed. Review the offers below — accept the one you like or counter any of them.'
          };
          setTurns(prev => [...prev, completeTurn]);
        }, 1200);
      }
    }, 1400);
  };
  useEffect(() => () => {
    if (bidIntervalRef.current) clearInterval(bidIntervalRef.current);
  }, []);
  const acceptBid = (bid: BidEntry) => {
    confirmBooking(bid.carrier, bid.price, '20 min');
  };
  const counterBid = (bid: BidEntry) => {
    if (chatExpired) return;
    const pseudoOffer: CarrierOffer = {
      id: bid.id,
      name: bid.carrier,
      rating: bid.rating,
      reviews: 0,
      vehicle: 'Carrier vehicle',
      price: bid.price,
      eta: '20 min'
    };
    setCounterSheetFor(pseudoOffer);
    setCounterPrice(String(Math.max(Math.round(bid.price * 0.9), 50)));
    setCounterNote('');
  };
  return <div className="flex flex-col h-full bg-gray-50 relative" data-id="element-2502">
      {/* Header */}
      <div className="px-3 py-3 border-b border-gray-100 bg-white/95 backdrop-blur-md shrink-0 z-10 sticky top-0" data-id="element-2503">
        <div className="flex items-center justify-between gap-2" data-id="element-2504">
          <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-2505">
            <button onClick={onBack} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-700 transition-colors shrink-0" aria-label="Back to chats" data-id="element-2506">
              <ChevronLeft className="w-5 h-5" data-id="element-2507" />
            </button>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${SERVICE_COLOR_MAP[serviceId] || 'bg-gray-100 text-gray-600'}`} data-id="element-2508">
              {(() => {
              const Icon = SERVICE_ICON_MAP[serviceId] || MessageCircle;
              return <Icon className="w-4 h-4" data-id="element-2509" />;
            })()}
            </div>
            <div className="min-w-0" data-id="element-2510">
              <p className="font-bold text-gray-900 text-[13px] leading-tight truncate" data-id="element-2511">
                {service.name}
              </p>
              <p className="text-[10px] text-brand-600 font-medium truncate" data-id="element-2512">
                Pair2 AI · {formatRelativeTime(createdAt)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0" data-id="element-2513">
            {!isDone && <div className="flex items-center gap-1" data-id="element-2514">
                {slots.filter(s => !s.optional).map(s => {
              const isFilled = values[s.id] !== undefined && values[s.id] !== '';
              return <div key={s.id} className={`w-1.5 h-1.5 rounded-full ${isFilled ? 'bg-brand-500' : 'bg-gray-200'}`} data-id="element-2515" />;
            })}
              </div>}
            <button onClick={onNewChat} className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center transition-colors" title="New request" aria-label="New request" data-id="element-2516">
              <Plus className="w-4 h-4" data-id="element-2517" />
            </button>
          </div>
        </div>
        {chatExpired && <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-2518">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" data-id="element-2519" />
            <div data-id="element-2520">
              <p className="text-[11px] font-bold text-amber-800 leading-tight" data-id="element-2521">
                This request is over 24 hours old
              </p>
              <p className="text-[10px] text-amber-700 leading-tight mt-0.5" data-id="element-2522">
                Quotes have expired. Start a new request to book.
              </p>
            </div>
          </div>}
      </div>

      {/* Chat Area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-hide pb-32" data-id="element-2523">
        {turns.map(m => m.role === 'ai' ? <div key={m.id} className="flex gap-2 max-w-[90%]" data-id="element-2524">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shrink-0 mt-1" data-id="element-2525">
                <Sparkles className="w-3 h-3 text-white" data-id="element-2526" />
              </div>
              <div className="space-y-2" data-id="element-2527">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm" data-id="element-2528">
                  <p className="text-[13px] text-gray-800 leading-relaxed whitespace-pre-wrap" data-id="element-2529">
                    {m.text}
                  </p>
                </div>
                {m.showUploader && !isDone && <button onClick={handlePhotoUpload} className="flex items-center justify-center gap-2 w-full py-2.5 bg-white border border-dashed border-gray-300 rounded-xl text-brand-600 text-xs font-bold hover:bg-brand-50 transition-colors" data-id="element-2530">
                    <Camera className="w-4 h-4" data-id="element-2531" />
                    Take Photo
                  </button>}
                {m.suggestions && !isDone && <div className="flex flex-wrap gap-1.5 pt-1" data-id="element-2532">
                    {m.suggestions.map(s => <button key={s} onClick={() => submitReply(s)} className="px-3 py-1.5 bg-white border border-brand-200 text-brand-700 rounded-full text-[11px] font-medium shadow-sm active:scale-95 transition-transform" data-id="element-2533">
                        {s}
                      </button>)}
                  </div>}
              </div>
            </div> : <div key={m.id} className="flex justify-end" data-id="element-2534">
              <div className="space-y-2 max-w-[85%]" data-id="element-2535">
                {m.text && <div className="bg-brand-500 rounded-2xl rounded-tr-sm px-3.5 py-2.5 shadow-sm" data-id="element-2536">
                    <p className="text-[13px] text-white leading-relaxed" data-id="element-2537">
                      {m.text}
                    </p>
                  </div>}
                {m.attachments?.map((a, i) => <img key={i} src={a.url} alt="Upload" className="w-40 h-32 object-cover rounded-xl border-2 border-white shadow-sm" data-id="element-2538" />)}
              </div>
            </div>)}

        {thinking && <div className="flex gap-2 max-w-[90%]" data-id="element-2539">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shrink-0 mt-1" data-id="element-2540">
              <Sparkles className="w-3 h-3 text-white" data-id="element-2541" />
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm flex items-center gap-1.5" data-id="element-2542">
              <motion.span className="w-1.5 h-1.5 rounded-full bg-gray-400" animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: 0
          }} data-id="element-2543" />
              <motion.span className="w-1.5 h-1.5 rounded-full bg-gray-400" animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: 0.15
          }} data-id="element-2544" />
              <motion.span className="w-1.5 h-1.5 rounded-full bg-gray-400" animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: 0.3
          }} data-id="element-2545" />
            </div>
          </div>}

        {/* Offers + Negotiation + Bidding + Confirmation */}
        {isDone && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="pt-4 space-y-4" data-id="element-2546">
            {/* OFFERS LIST — kept visible during bidding so user can scroll back */}
            {(offerStage === 'offers' || offerStage === 'bidding-live' || offerStage === 'bidding-complete') && <>
                <div className="flex items-center gap-2 px-2" data-id="element-2547">
                  <div className="h-px flex-1 bg-gray-200" data-id="element-2548" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2549">
                    {offerStage === 'offers' ? `${MOCK_OFFERS.length} Carriers Available` : 'Initial offers'}
                  </span>
                  <div className="h-px flex-1 bg-gray-200" data-id="element-2550" />
                </div>

                {MOCK_OFFERS.map((offer, idx) => <motion.div key={offer.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.07
          }} className="bg-white rounded-2xl p-3.5 border border-gray-200 shadow-sm" data-id="element-2551">
                    <div className="flex justify-between items-start mb-2.5" data-id="element-2552">
                      <div className="min-w-0 flex-1" data-id="element-2553">
                        <div className="flex items-center gap-1.5 mb-0.5" data-id="element-2554">
                          <h4 className="font-bold text-gray-900 text-[13px] truncate" data-id="element-2555">
                            {offer.name}
                          </h4>
                          {offer.verified && <Shield className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-2556" />}
                          {offer.fast && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700" data-id="element-2557">
                              Fast
                            </span>}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500" data-id="element-2558">
                          <span className="flex items-center gap-0.5" data-id="element-2559">
                            <Star className="w-2.5 h-2.5 text-amber-400 fill-current" data-id="element-2560" />
                            {offer.rating} ({offer.reviews})
                          </span>
                          <span data-id="element-2561">·</span>
                          <span data-id="element-2562">{offer.vehicle}</span>
                          <span data-id="element-2563">·</span>
                          <span className="flex items-center gap-0.5" data-id="element-2564">
                            <Clock className="w-2.5 h-2.5" data-id="element-2565" />
                            {offer.eta}
                          </span>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-3" data-id="element-2566">
                        <p className="text-lg font-display font-bold text-gray-900 leading-none" data-id="element-2567">
                          AED {offer.price}
                        </p>
                      </div>
                    </div>
                    {offerStage === 'offers' ? <div className="grid grid-cols-2 gap-2" data-id="element-2568">
                        <button disabled={chatExpired} onClick={() => confirmBooking(offer.name, offer.price, offer.eta)} className="py-2 bg-gray-900 text-white rounded-xl text-xs font-bold active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2569">
                          Accept
                        </button>
                        <button disabled={chatExpired} onClick={() => openCounterSheet(offer)} className="py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2570">
                          Counter
                        </button>
                      </div> : <div className="text-[10px] text-gray-400 italic px-1" data-id="element-2571">
                        Superseded — bidding now open
                      </div>}
                  </motion.div>)}

                {/* Open for Bidding card — only when no bidding round yet */}
                {offerStage === 'offers' && <div className="pt-1" data-id="element-2572">
                    <div className="flex items-center gap-2 px-2 mb-3" data-id="element-2573">
                      <div className="h-px flex-1 bg-gray-200" data-id="element-2574" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2575">
                        Or save more
                      </span>
                      <div className="h-px flex-1 bg-gray-200" data-id="element-2576" />
                    </div>
                    <button disabled={chatExpired} onClick={() => setBiddingSheetOpen(true)} className="w-full bg-gradient-to-br from-brand-500 to-brand-600 text-white rounded-2xl p-4 text-left shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2577">
                      <div className="flex items-start justify-between gap-3" data-id="element-2578">
                        <div data-id="element-2579">
                          <div className="flex items-center gap-1.5 mb-1" data-id="element-2580">
                            <ListChecks className="w-4 h-4" data-id="element-2581" />
                            <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-2582">
                              Smart Save
                            </span>
                          </div>
                          <h4 className="font-bold text-sm" data-id="element-2583">
                            Open for Bidding
                          </h4>
                          <p className="text-[11px] text-white/85 mt-0.5" data-id="element-2584">
                            Let carriers compete · save ~18%
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 mt-1" data-id="element-2585" />
                      </div>
                    </button>
                  </div>}
              </>}

            {/* COUNTER WAITING */}
            {offerStage === 'counter-waiting' && negotiationCarrier && <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm" data-id="element-2586">
                <div className="flex items-center gap-3" data-id="element-2587">
                  <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center shrink-0" data-id="element-2588">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-2589">
                      <Loader2 className="w-5 h-5 text-brand-500" data-id="element-2590" />
                    </motion.div>
                  </div>
                  <div className="min-w-0" data-id="element-2591">
                    <p className="text-sm font-bold text-gray-900" data-id="element-2592">
                      Negotiating…
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-2593">
                      Waiting on {negotiationCarrier.name}
                    </p>
                  </div>
                </div>
              </div>}

            {/* COUNTER RESPONSE */}
            {offerStage === 'counter-response' && negotiationCarrier && carrierCounterPrice != null && <div className="bg-white rounded-2xl p-4 border-2 border-brand-200 shadow-sm" data-id="element-2594">
                  <div className="flex items-center justify-between mb-3" data-id="element-2595">
                    <div data-id="element-2596">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600 mb-0.5" data-id="element-2597">
                        {negotiationCarrier.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2598">
                        Round {counterRound} of 3
                      </p>
                    </div>
                    <div className="text-right" data-id="element-2599">
                      <p className="text-[10px] text-gray-400 line-through" data-id="element-2600">
                        AED {negotiationCarrier.price}
                      </p>
                      <p className="text-xl font-display font-bold text-brand-600 leading-none" data-id="element-2601">
                        AED {carrierCounterPrice}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-2602">
                    <button disabled={chatExpired} onClick={acceptCarrierCounter} className="w-full py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2603">
                      Accept AED {carrierCounterPrice}
                    </button>
                    <div className="grid grid-cols-2 gap-2" data-id="element-2604">
                      <button disabled={counterRound >= 3 || chatExpired} onClick={counterAgain} className="py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 rounded-xl text-xs font-bold transition-colors" data-id="element-2605">
                        Counter again
                      </button>
                      <button onClick={cancelNegotiation} className="py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-bold hover:bg-gray-50 transition-colors" data-id="element-2606">
                        Back to offers
                      </button>
                    </div>
                  </div>
                </div>}

            {/* BIDDING LIVE */}
            {offerStage === 'bidding-live' && <div className="bg-white rounded-2xl p-4 border border-brand-200 shadow-sm" data-id="element-2607">
                <div className="flex items-center justify-between mb-4" data-id="element-2608">
                  <div className="flex items-center gap-2" data-id="element-2609">
                    <span className="relative flex h-2 w-2" data-id="element-2610">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" data-id="element-2611" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" data-id="element-2612" />
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-900" data-id="element-2613">
                      Live Bidding
                    </span>
                  </div>
                  <span className="text-[11px] text-gray-500 font-medium" data-id="element-2614">
                    {liveBids.length} bid
                    {liveBids.length === 1 ? '' : 's'} in
                  </span>
                </div>
                <div className="space-y-2" data-id="element-2615">
                  <AnimatePresence data-id="element-2616">
                    {liveBids.map(bid => <motion.div key={bid.id} initial={{
                opacity: 0,
                x: -20,
                scale: 0.95
              }} animate={{
                opacity: 1,
                x: 0,
                scale: 1
              }} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl" data-id="element-2617">
                        <div className="min-w-0" data-id="element-2618">
                          <p className="text-xs font-bold text-gray-900 truncate" data-id="element-2619">
                            {bid.carrier}
                          </p>
                          <p className="text-[10px] text-gray-500 flex items-center gap-0.5" data-id="element-2620">
                            <Star className="w-2.5 h-2.5 text-amber-400 fill-current" data-id="element-2621" />
                            {bid.rating}
                          </p>
                        </div>
                        <span className="text-sm font-bold text-gray-900 ml-2" data-id="element-2622">
                          AED {bid.price}
                        </span>
                      </motion.div>)}
                  </AnimatePresence>
                  {liveBids.length === 0 && <div className="text-center py-4 text-[11px] text-gray-400" data-id="element-2623">
                      Waiting for carriers to bid…
                    </div>}
                </div>
              </div>}

            {/* BIDDING COMPLETE — Accept or Counter on each bid */}
            {offerStage === 'bidding-complete' && <>
                <div className="flex items-center gap-2 px-2" data-id="element-2624">
                  <div className="h-px flex-1 bg-gray-200" data-id="element-2625" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600" data-id="element-2626">
                    Bidding closed · {liveBids.length} offers
                  </span>
                  <div className="h-px flex-1 bg-gray-200" data-id="element-2627" />
                </div>
                {[...liveBids].sort((a, b) => a.price - b.price).map((bid, idx) => <motion.div key={bid.id} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.06
          }} className={`bg-white rounded-2xl p-3.5 border shadow-sm ${idx === 0 ? 'border-2 border-emerald-400' : 'border-gray-200'}`} data-id="element-2628">
                      <div className="flex justify-between items-start mb-2.5" data-id="element-2629">
                        <div className="min-w-0" data-id="element-2630">
                          <div className="flex items-center gap-1.5" data-id="element-2631">
                            <h4 className="font-bold text-gray-900 text-[13px]" data-id="element-2632">
                              {bid.carrier}
                            </h4>
                            {idx === 0 && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" data-id="element-2633">
                                Best Price
                              </span>}
                          </div>
                          <p className="text-[10px] text-gray-500 flex items-center gap-0.5 mt-0.5" data-id="element-2634">
                            <Star className="w-2.5 h-2.5 text-amber-400 fill-current" data-id="element-2635" />
                            {bid.rating}
                          </p>
                        </div>
                        <p className="text-lg font-display font-bold text-gray-900" data-id="element-2636">
                          AED {bid.price}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-2" data-id="element-2637">
                        <button disabled={chatExpired} onClick={() => acceptBid(bid)} className={`py-2 rounded-xl text-xs font-bold active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed ${idx === 0 ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-gray-900 hover:bg-black text-white'}`} data-id="element-2638">
                          Accept
                        </button>
                        <button disabled={chatExpired} onClick={() => counterBid(bid)} className="py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2639">
                          Counter
                        </button>
                      </div>
                    </motion.div>)}
                <button onClick={() => {
            setOfferStage('offers');
            setLiveBids([]);
          }} className="w-full py-2 text-xs font-bold text-gray-500 hover:text-gray-700" data-id="element-2640">
                  ← Back to direct offers
                </button>
              </>}

            {/* CONFIRMED — booking success */}
            {offerStage === 'confirmed' && confirmedBooking && <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5 border border-emerald-200" data-id="element-2641">
                <div className="flex items-center gap-3 mb-4" data-id="element-2642">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0" data-id="element-2643">
                    <CheckCircle2 className="w-5 h-5" data-id="element-2644" />
                  </div>
                  <div className="min-w-0" data-id="element-2645">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700" data-id="element-2646">
                      Booking confirmed
                    </p>
                    <p className="text-sm font-bold text-gray-900 truncate" data-id="element-2647">
                      {confirmedBooking.carrier}
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-3 mb-4 space-y-1.5" data-id="element-2648">
                  <div className="flex justify-between text-xs" data-id="element-2649">
                    <span className="text-gray-500" data-id="element-2650">Trip ID</span>
                    <span className="font-bold text-gray-900" data-id="element-2651">
                      {confirmedBooking.tripId}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs" data-id="element-2652">
                    <span className="text-gray-500" data-id="element-2653">ETA</span>
                    <span className="font-bold text-gray-900" data-id="element-2654">
                      {confirmedBooking.eta}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs" data-id="element-2655">
                    <span className="text-gray-500" data-id="element-2656">Total</span>
                    <span className="font-bold text-gray-900" data-id="element-2657">
                      AED {confirmedBooking.price}
                    </span>
                  </div>
                </div>
                <button onClick={onViewJobs} className="w-full py-2.5 bg-gray-900 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform" data-id="element-2658">
                  View in Jobs
                  <ArrowRight className="w-4 h-4" data-id="element-2659" />
                </button>
              </motion.div>}
          </motion.div>}
      </div>
    </div>;
}